import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import AppShell from '@/components/AppShell';
import ModuleTile from '@/components/ModuleTile';
import { Gem, Sparkles, Circle, Crown, Hammer, Coins, Building2, ScrollText, Users, AlertCircle, Inbox, ChevronDown, ChevronUp, Shield, Check, Timer, AlertTriangle, ArrowRight, ClipboardList, FileDown, X, Filter, Plus, Search, LayoutGrid, Table as TableIcon, Image as ImageIcon, Loader2, Flag, EyeOff, RefreshCw, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useNotifications } from '@/lib/notifications';
import { formatINR, formatINRCompact, formatCarats, formatGrams, formatDateTime } from '@/lib/format';
import { Card } from '@/components/ui/card';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { DelayBadge, DELAY_COLORS, formatHours } from '@/components/production/DelayBadge';
import { sortDiamondItems as _sortDiamondItems } from '@/lib/diamondHierarchy';
import { useReferenceConfig } from '@/hooks/useReferenceConfig';
import { InlineReference } from '@/components/InlineReference';

function NotificationsEntryCard() {
  const { inboxUnread, sentUnread, priorityUnread, viewableOnce } = useNotifications();
  const navigate = useNavigate();
  const go = (url) => (e) => { e.stopPropagation(); navigate(url); };
  return (
    <Card className="rounded-xl border bg-white p-4 flex items-start gap-4" data-testid="dashboard-notifications-entry">
      <Link to="/notifications" className="shrink-0" aria-label="Open notifications">
        <div className="relative h-11 w-11 rounded-xl bg-[var(--diamond-bg)] border border-[var(--diamond-border)] flex items-center justify-center">
          <Inbox size={20} className="text-[var(--diamond-icon)]" />
          {inboxUnread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-medium flex items-center justify-center border-2 border-white">
              {inboxUnread > 99 ? '99+' : inboxUnread}
            </span>
          )}
        </div>
      </Link>
      <div className="min-w-0 flex-1">
        <Link to="/notifications" className="block group">
          <div className="font-medium text-[var(--ink-navy)] group-hover:underline">Notifications</div>
          <div className="text-sm text-slate-500">
            {inboxUnread > 0 ? `${inboxUnread} unread in your inbox` : 'You’re all caught up'}
            {sentUnread > 0 ? ` · ${sentUnread} sent awaiting reads` : ''}
          </div>
        </Link>
        {(priorityUnread > 0 || viewableOnce > 0) && (
          <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="dashboard-notification-chips">
            {priorityUnread > 0 && (
              <button
                type="button"
                onClick={go('/notifications?tab=inbox&filter=priority')}
                data-testid="dashboard-chip-priority"
                title="View unread priority mails"
                className="inline-flex items-center gap-1 h-6 pl-2 pr-2.5 rounded-full bg-rose-600 text-white text-[11px] font-semibold hover:bg-rose-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 transition-colors"
              >
                <Flag size={11} /> {priorityUnread} Priority
              </button>
            )}
            {viewableOnce > 0 && (
              <button
                type="button"
                onClick={go('/notifications?tab=inbox&filter=disappearing')}
                data-testid="dashboard-chip-viewable-once"
                title="View viewable-once mails"
                className="inline-flex items-center gap-1 h-6 pl-2 pr-2.5 rounded-full bg-violet-600 text-white text-[11px] font-semibold hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 transition-colors"
              >
                <EyeOff size={11} /> {viewableOnce} Viewable Once
              </button>
            )}
          </div>
        )}
      </div>
      <Button variant="outline" size="sm" className="shrink-0" onClick={() => navigate('/notifications')} data-testid="dashboard-open-notifications">
        Open <ArrowRight size={14} className="ml-1.5" />
      </Button>
    </Card>
  );
}


export default function Dashboard() {
  const { user, isSuperAdmin, isCompanyAdmin, isSalesRole, effectiveCompanyId, canViewValues, hasMaterialAccess } = useAuth();
  // Wave 4.x — Admins (Super Admin / Company Admin) get the "Requests" tile
  // pinned beside Notifications at the top of the dashboard.
  const isAdmin = isSuperAdmin || isCompanyAdmin;
  // Wave 4.x — Dashboard-wide refresh. Every data-driven tile & widget
  // (KPIs, module tiles, Pending Requirements, Requests, Recent Activity,
  // Production Timeline) refetches when ``refreshNonce`` changes. It ticks
  // automatically every 5 minutes and can be fired on demand via the
  // "Refresh now" button. ``lastRefreshedAt`` drives the live countdown.
  const REFRESH_MS = 5 * 60 * 1000;
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(() => Date.now());
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [refreshing, setRefreshing] = useState(false);
  const refreshSpinTimer = useRef(null);

  const triggerRefresh = useCallback(() => {
    setRefreshNonce((n) => n + 1);
    setLastRefreshedAt(Date.now());
    setRefreshing(true);
    if (refreshSpinTimer.current) window.clearTimeout(refreshSpinTimer.current);
    refreshSpinTimer.current = window.setTimeout(() => setRefreshing(false), 900);
  }, []);

  // Auto-refresh every 5 min. Restarts whenever ``lastRefreshedAt`` changes
  // (a manual refresh) so the next auto tick is always a full 5 min away.
  useEffect(() => {
    const id = window.setInterval(triggerRefresh, REFRESH_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerRefresh, lastRefreshedAt]);

  // 1-second tick powering the countdown label.
  useEffect(() => {
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => () => { if (refreshSpinTimer.current) window.clearTimeout(refreshSpinTimer.current); }, []);

  const msLeft = Math.max(0, REFRESH_MS - (nowTs - lastRefreshedAt));
  const countdownLabel = `${Math.floor(msLeft / 60000)}:${String(Math.floor((msLeft % 60000) / 1000)).padStart(2, '0')}`;

  // Reusable refresh bar (used on both the admin/company and sales dashboards).
  const refreshBar = (
    <div className="mb-4 flex items-center justify-end gap-3" data-testid="dashboard-refresh-bar">
      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid="dashboard-refresh-timer">
        <Clock size={13} className="text-slate-400" />
        Auto-refresh in
        <span className="font-mono tabular-nums text-slate-700">{countdownLabel}</span>
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={triggerRefresh}
        className="h-8 gap-1.5 text-xs"
        data-testid="dashboard-refresh-now-button"
        title="Refresh all dashboard tiles & widgets now"
      >
        <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
        Refresh now
      </Button>
    </div>
  );

  // Wave 3.99l — Material access flags gate every raw-material tile /
  // KPI on the dashboard. Materials the user cannot ``see`` render as
  // "—" placeholders instead of leaking data via aggregated numbers.
  const canSeeGold = hasMaterialAccess('gold', 'see');
  const canSeeDiamond = hasMaterialAccess('diamond', 'see');
  const canSeeColorStone = hasMaterialAccess('color_stone', 'see');
  const canSeePearl = hasMaterialAccess('pearl', 'see');
  // Helpers that either format or return an "—" placeholder based on
  // access. Used across module tile stats + KPI aggregates.
  const scoped = (allowed, formatter) => (allowed ? formatter() : '—');
  const [stats, setStats] = useState({
    diamond_carats: 0, diamond_pieces: 0, diamond_value: 0,
    gold_grams: 0, gold_value: 0,
    open_orders: 0, completed_orders: 0, vendor_count: 0, goldsmith_count: 0,
    company_count: 0, user_count: 0,
    // Phase 15.9 — value of goods currently with goldsmiths company-wide
    goldsmith_stock_value: 0,
    goldsmith_stock_unattr_value: 0,
    goldsmith_stock_attr_value: 0,
    goldsmiths_with_balance: 0,
    // Phase 16.13 — Color Stones & Pearls inventory tiles
    cs_carats: 0, cs_pieces: 0, cs_value: 0,
    pearl_carats: 0, pearl_pieces: 0, pearl_value: 0,
    // Wave 3.24 — Jewellery tile
    jewellery_count: 0, jewellery_value: 0,
    // Wave 3.33 — Top-line KPI re-design
    //   * on_approval_* surfaces vendor-on-approval raw material currently
    //     under custody but not yet converted to stock (bifurcation by
    //     material type).
    //   * pending_requirements_count aggregates open requirements across
    //     all active orders so the operator sees outstanding work-to-do.
    on_approval_diamond_value: 0,
    on_approval_cs_value: 0,
    on_approval_pearl_value: 0,
    // Wave 3.41 — Count of packets on-approval per material so the
    // restricted-user dashboard (no values.view) can still show how many
    // packets are awaiting decision without revealing any rupee figures.
    on_approval_diamond_count: 0,
    on_approval_cs_count: 0,
    on_approval_pearl_count: 0,
    pending_requirements_count: 0,
    pending_requirements_orders: 0,
  });
  const [recentLogs, setRecentLogs] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const cid = effectiveCompanyId;
        const queries = [];
        if (cid) {
          queries.push(api.get('/diamonds/closing-balance', { params: { company_id: cid } }));
          queries.push(api.get('/gold/ledger/summary', { params: { company_id: cid } }));
          queries.push(api.get('/orders/', { params: { company_id: cid, status: 'open' } }));
          queries.push(api.get('/orders/', { params: { company_id: cid, status: 'completed' } }));
          queries.push(api.get('/masters/vendors', { params: { company_id: cid } }));
          queries.push(api.get('/masters/goldsmiths', { params: { company_id: cid } }));
          // Phase 15.9 — goldsmith stock value tile
          queries.push(api.get('/goldsmith-issues/stock-value-company', { params: { company_id: cid } }));
          // Phase 16.13 — Color Stones + Pearls closing-balance summaries
          queries.push(api.get('/color-stones/closing-balance', { params: { company_id: cid } }));
          queries.push(api.get('/pearls/closing-balance', { params: { company_id: cid } }));
          // Wave 3.24 — Jewellery aggregate stats
          queries.push(api.get('/jewellery/summary', { params: { company_id: cid } }));
          // Wave 4.08 — Pending Requirements KPI now reads the SAME server
          // source as the "Pending Requirements" list widget below, so the
          // top card and the list ALWAYS agree. This respects the caller's
          // material-view ("see") scope and excludes Cancelled/Completed
          // orders (active work only) — matching the list exactly.
          queries.push(api.get('/dashboard/pending-requirements', { params: { company_id: cid, count_only: true } }));
        }
        queries.push(api.get('/logs/', { params: { limit: 8, company_id: cid || undefined } }));
        if (isSuperAdmin) {
          queries.push(api.get('/companies/'));
          queries.push(api.get('/users/'));
        }
        const results = await Promise.allSettled(queries);

        let i = 0;
        const next = { ...stats };
        if (cid) {
          const cb = results[i++]?.value?.data;
          if (cb) {
            next.diamond_carats = cb.grand_total.carats;
            next.diamond_pieces = cb.grand_total.pieces;
            next.diamond_value = cb.grand_total.value;
            // Wave 3.33 — Sum on-approval packets across all classifications.
            let oaDia = 0;
            let oaDiaCount = 0;
            for (const node of (cb.tree || [])) {
              for (const p of (node.packets || [])) {
                if ((p.purchase_status || '') === 'on_approval') {
                  oaDia += Number(p.balance_value || 0);
                  oaDiaCount += 1;
                }
              }
            }
            next.on_approval_diamond_value = oaDia;
            next.on_approval_diamond_count = oaDiaCount;
          }
          const gs = results[i++]?.value?.data;
          if (gs) {
            next.gold_grams = gs.closing.pure_grams_999;
            next.gold_value = gs.closing.value;
          }
          const openO = results[i++]?.value?.data || [];
          next.open_orders = openO.length;
          // Wave 3.33.1 — Pending requirement counting moved below; we now
          // iterate the "all orders" list (see end of this block) so we
          // also catch incomplete requirements lurking in orders that the
          // user already marked as Completed.
          const compO = results[i++]?.value?.data || [];
          next.completed_orders = compO.length;
          const vens = results[i++]?.value?.data || [];
          next.vendor_count = vens.length;
          const gms = results[i++]?.value?.data || [];
          next.goldsmith_count = gms.length;
          // Phase 15.9 — Stock value with goldsmiths
          const gsv = results[i++]?.value?.data;
          if (gsv) {
            next.goldsmith_stock_value = Number(gsv.total_value || 0);
            next.goldsmith_stock_unattr_value = Number(gsv.total_unattributed_value || 0);
            next.goldsmith_stock_attr_value = Number(gsv.total_attributed_value || 0);
            next.goldsmiths_with_balance = Number(gsv.goldsmiths_with_balance_count || 0);
          }
          // Phase 16.13 — Color Stones closing balance
          const cscb = results[i++]?.value?.data;
          if (cscb) {
            next.cs_carats = Number(cscb.grand_total?.carats || 0);
            next.cs_pieces = Number(cscb.grand_total?.pieces || 0);
            next.cs_value = Number(cscb.grand_total?.value || 0);
            // Wave 3.33 — On-approval color stones
            let oaCs = 0;
            let oaCsCount = 0;
            for (const node of (cscb.tree || [])) {
              for (const p of (node.packets || [])) {
                if ((p.purchase_status || '') === 'on_approval') {
                  oaCs += Number(p.balance_value || 0);
                  oaCsCount += 1;
                }
              }
            }
            next.on_approval_cs_value = oaCs;
            next.on_approval_cs_count = oaCsCount;
          }
          // Phase 16.13 — Pearls closing balance
          const prcb = results[i++]?.value?.data;
          if (prcb) {
            next.pearl_carats = Number(prcb.grand_total?.carats || 0);
            next.pearl_pieces = Number(prcb.grand_total?.pieces || 0);
            next.pearl_value = Number(prcb.grand_total?.value || 0);
            // Wave 3.33 — On-approval pearls
            let oaPe = 0;
            let oaPeCount = 0;
            for (const node of (prcb.tree || [])) {
              for (const p of (node.packets || [])) {
                if ((p.purchase_status || '') === 'on_approval') {
                  oaPe += Number(p.balance_value || 0);
                  oaPeCount += 1;
                }
              }
            }
            next.on_approval_pearl_value = oaPe;
            next.on_approval_pearl_count = oaPeCount;
          }
          // Wave 3.24 — Jewellery summary
          const jsum = results[i++]?.value?.data;
          if (jsum) {
            next.jewellery_count = Number(jsum.count || 0);
            next.jewellery_value = Number(jsum.total_cost_value || 0);
          }
          // Wave 4.08 — Read the cheap server ``count_only`` totals so the
          // KPI card mirrors the Pending Requirements list EXACTLY (same
          // material "see" scope + active-order-only filter). This replaces
          // the old client-side counter that scanned every order (any stage)
          // across all materials and therefore over-counted vs the list.
          const pendRes = results[i++]?.value?.data || {};
          next.pending_requirements_count = Number(pendRes.total_pending || 0);
          next.pending_requirements_orders = Number(pendRes.order_count || 0);
        }
        setRecentLogs(results[i++]?.value?.data || []);
        if (isSuperAdmin) {
          const cos = results[i++]?.value?.data || [];
          next.company_count = cos.length;
          const uss = results[i++]?.value?.data || [];
          next.user_count = uss.length;
        }
        setStats(next);
      } catch (e) { /* ignore */ }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCompanyId, refreshNonce]);

  // Wave 3.33 — Top KPI tiles redesigned to avoid duplicating data already
  // present in the Module tiles below.
  //   1) Total Inventory Value — Jewellery + Raw Material breakdown
  //   2) Material with Goldsmiths (WIP) — money tied up outside the office
  //   3) On-Approval Material — vendor-held stock not yet converted
  //   4) Pending Requirements — operational work-to-do counter
  // Wave 3.99l — Only sum in materials the operator can actually see so
  // aggregated KPIs never leak restricted totals.
  const rawMaterialValue = (canSeeGold ? (stats.gold_value || 0) : 0)
    + (canSeeDiamond ? (stats.diamond_value || 0) : 0)
    + (canSeeColorStone ? (stats.cs_value || 0) : 0)
    + (canSeePearl ? (stats.pearl_value || 0) : 0);
  const totalInventoryValue = (stats.jewellery_value || 0) + rawMaterialValue;
  const onApprovalTotal = (canSeeDiamond ? (stats.on_approval_diamond_value || 0) : 0)
    + (canSeeColorStone ? (stats.on_approval_cs_value || 0) : 0)
    + (canSeePearl ? (stats.on_approval_pearl_value || 0) : 0);
  // On-approval bifurcation strings honour scope: restricted materials
  // render as "—" so the KPI subtitle doesn't hint at their values.
  const onApprovalSub = `Dia ${canSeeDiamond ? formatINRCompact(stats.on_approval_diamond_value) : '—'} · CS ${canSeeColorStone ? formatINRCompact(stats.on_approval_cs_value) : '—'} · Pearl ${canSeePearl ? formatINRCompact(stats.on_approval_pearl_value) : '—'}`;
  // Quantity KPI for value-restricted users. Restricted-material carats
  // are excluded from the total; if user has no stone visibility we
  // fall back to the "—" placeholder.
  const stoneCaratsTotal =
      (canSeeDiamond ? (stats.diamond_carats || 0) : 0)
    + (canSeeColorStone ? (stats.cs_carats || 0) : 0)
    + (canSeePearl ? (stats.pearl_carats || 0) : 0);
  const hasAnyStoneAccess = canSeeDiamond || canSeeColorStone || canSeePearl;
  const rawMaterialQtyValue = `${canSeeGold ? formatGrams(stats.gold_grams) : '—'} · ${hasAnyStoneAccess ? formatCarats(stoneCaratsTotal) : '—'}`;
  // Restricted on-approval count (for value-restricted users).
  const onApprovalCountRestricted =
      (canSeeDiamond ? (stats.on_approval_diamond_count || 0) : 0)
    + (canSeeColorStone ? (stats.on_approval_cs_count || 0) : 0)
    + (canSeePearl ? (stats.on_approval_pearl_count || 0) : 0);

  const kpis = effectiveCompanyId ? (canViewValues ? [
    {
      label: 'Total Inventory Value',
      value: formatINR(totalInventoryValue, { decimals: 0 }),
      sub: `Jewellery ${formatINRCompact(stats.jewellery_value)} · Raw Material ${formatINRCompact(rawMaterialValue)}`,
      testid: 'dashboard-kpi-total-inventory',
    },
    {
      label: 'Material with Goldsmiths (WIP)',
      value: formatINR(stats.goldsmith_stock_value, { decimals: 0 }),
      sub: `Across ${stats.goldsmiths_with_balance || 0} goldsmith${(stats.goldsmiths_with_balance || 0) === 1 ? '' : 's'}`,
      testid: 'dashboard-kpi-wip-value',
    },
    {
      label: 'On-Approval Material',
      value: formatINR(onApprovalTotal, { decimals: 0 }),
      sub: onApprovalSub,
      testid: 'dashboard-kpi-on-approval',
    },
    {
      label: 'Pending Requirements',
      value: stats.pending_requirements_count,
      sub: stats.pending_requirements_orders === 0
        ? 'No outstanding work'
        : `Across ${stats.pending_requirements_orders} order${stats.pending_requirements_orders === 1 ? '' : 's'}`,
      testid: 'dashboard-kpi-pending-requirements',
    },
  ] : [
    // Wave 3.41 — Restricted users (no values.view) see quantity-only
    // equivalents of the monetary KPIs. This keeps the operational story
    // intact (how much material, where, what's pending) while removing
    // every rupee figure.
    {
      label: 'Raw Material on Hand',
      value: rawMaterialQtyValue,
      sub: `Gold · Dia + CS + Pearl combined`,
      testid: 'dashboard-kpi-raw-material-qty',
    },
    {
      label: 'Material with Goldsmiths (WIP)',
      value: stats.goldsmiths_with_balance || 0,
      sub: `Goldsmith${(stats.goldsmiths_with_balance || 0) === 1 ? '' : 's'} currently holding material`,
      testid: 'dashboard-kpi-wip-count',
    },
    {
      label: 'On-Approval Material',
      value: onApprovalCountRestricted,
      sub: 'Packets pending conversion or return',
      testid: 'dashboard-kpi-on-approval-count',
    },
    {
      label: 'Pending Requirements',
      value: stats.pending_requirements_count,
      sub: stats.pending_requirements_orders === 0
        ? 'No outstanding work'
        : `Across ${stats.pending_requirements_orders} order${stats.pending_requirements_orders === 1 ? '' : 's'}`,
      testid: 'dashboard-kpi-pending-requirements',
    },
  ]) : [
    { label: 'Companies', value: stats.company_count, sub: 'In portal', testid: 'dashboard-kpi-company-count' },
    { label: 'Users', value: stats.user_count, sub: 'Across all companies', testid: 'dashboard-kpi-user-count' },
    { label: 'Recent Activity', value: recentLogs.length, sub: 'Latest events', testid: 'dashboard-kpi-recent-activity' },
    { label: 'Status', value: 'Healthy', sub: 'All services operational', testid: 'dashboard-kpi-status' },
  ];

  // Sales roles (Sales Person / Sales Manager): a focused dashboard showing
  // ONLY the Pending Requirements of their attributed customers' active
  // orders. No module tiles, KPIs, timelines, or exports.
  if (isSalesRole) {
    return (
      <AppShell
        title={`Welcome, ${user?.name?.split(' ')[0] || 'there'}`}
        subtitle="Pending requirements across your customers' active orders"
      >
        <div className="space-y-6">
          {refreshBar}
          <NotificationsEntryCard />
          {effectiveCompanyId ? (
            <PendingRequirementsByOrderWidget companyId={effectiveCompanyId} hideExport refreshSignal={refreshNonce} />
          ) : (
            <div className="text-sm text-slate-500" data-testid="sales-dashboard-no-company">No company context available.</div>
          )}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={`Welcome, ${user?.name?.split(' ')[0] || 'there'}`}
      subtitle={isSuperAdmin ? (effectiveCompanyId ? 'Viewing company-scoped data' : 'System-wide view · Select a company to manage inventory') : 'Today’s overview'}
    >
      {/* Wave 4.x — Dashboard refresh bar. Live countdown to the next auto
          refresh (every 5 min) + a manual "Refresh now" that reloads every
          tile & widget on the page. */}
      {refreshBar}
      {/* Notifications entry — sits at the very top of the dashboard.
          Wave 4.x — For admins (Super Admin / Company Admin) the "Requests"
          tile sits RIGHT BESIDE Notifications so pending team requests
          (delete / attention) are impossible to miss. */}
      {isAdmin ? (
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <NotificationsEntryCard />
          <DeleteAndAttentionWidget isSuperAdmin={isSuperAdmin} refreshSignal={refreshNonce} />
        </div>
      ) : (
        <div className="mb-6">
          <NotificationsEntryCard />
        </div>
      )}
      {/* KPI row — responsive: 1 col phone, 2 cols small laptop, 4 cols ≥xl (1280px+)
          Cards have generous padding + min-height + tabular-nums so Indian
          numbers (lakhs / crores) don't break the layout. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        {kpis.map((k, i) => (
          <div key={i} className="kpi-card" data-testid={k.testid}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{k.label}</div>
            {/* Wave 3.31 — Responsive font + breakable value so Indian
                lakhs/crores fit on small laptops (1024-1279px) without
                truncation. The leading also reduces at narrow widths. */}
            <div
              className="font-mono tabular-nums text-2xl sm:text-3xl lg:text-[22px] xl:text-2xl 2xl:text-3xl text-[var(--ink-navy)] mt-2 kpi-value leading-tight"
              title={typeof k.value === 'string' ? k.value : String(k.value)}
              data-testid={`${k.testid}-value`}
            >
              {k.value}
            </div>
            <div className="text-xs text-slate-500 mt-2">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Module tiles */}
      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <div className="font-serif-display text-2xl text-[var(--ink-navy)]">Modules</div>
          <div className="text-xs text-slate-500">Navigate inventory categories</div>
        </div>
      </div>
      {/* Module tiles — responsive: 1 (phone) / 2 (sm) / 3 (lg) / 6 (2xl, ≥1536px)
          On small laptops (1280-1535px) shows 3 across so currency values
          in inner stat boxes have room to breathe.

          Wave 3.41 — When the user lacks ``values.view`` the inner ``stats``
          arrays drop their Value/Cost rows so module tiles only surface
          quantity (grams / carats / pieces / count). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-3 md:gap-4">
        <ModuleTile palette="gold" icon={Coins} title="Gold (999)"
          subtitle="Purities, rates, ledger"
          to={canSeeGold ? "/gold" : undefined}
          testid="dashboard-module-gold-tile"
          stats={canViewValues
            ? [{ label: 'Grams', value: canSeeGold ? formatGrams(stats.gold_grams) : '—' }, { label: 'Value', value: canSeeGold ? formatINR(stats.gold_value) : '—' }]
            : [{ label: 'Grams', value: canSeeGold ? formatGrams(stats.gold_grams) : '—' }]}
        />
        <ModuleTile palette="diamond" icon={Gem} title="Loose Diamonds"
          subtitle="Packets, ledgers, valuations"
          to={canSeeDiamond ? "/diamonds" : undefined}
          testid="dashboard-module-diamonds-tile"
          stats={canViewValues ? [
            { label: 'Carats', value: canSeeDiamond ? formatCarats(stats.diamond_carats) : '—' },
            { label: 'Value', value: canSeeDiamond ? formatINR(stats.diamond_value) : '—' },
          ] : [
            { label: 'Carats', value: canSeeDiamond ? formatCarats(stats.diamond_carats) : '—' },
            { label: 'Pieces', value: canSeeDiamond ? (stats.diamond_pieces || 0) : '—' },
          ]}
        />
        <ModuleTile palette="colorstone" icon={Sparkles} title="Color Stones"
          subtitle="Packets, ledgers, valuations"
          to={canSeeColorStone ? "/color-stones" : undefined}
          testid="dashboard-module-color-stones-tile"
          stats={canViewValues ? [
            { label: 'Carats', value: canSeeColorStone ? formatCarats(stats.cs_carats) : '—' },
            { label: 'Value', value: canSeeColorStone ? formatINR(stats.cs_value) : '—' },
          ] : [
            { label: 'Carats', value: canSeeColorStone ? formatCarats(stats.cs_carats) : '—' },
            { label: 'Pieces', value: canSeeColorStone ? (stats.cs_pieces || 0) : '—' },
          ]}
        />
        <ModuleTile palette="pearl" icon={Circle} title="Pearls"
          subtitle="Packets, ledgers, valuations"
          to={canSeePearl ? "/pearls" : undefined}
          testid="dashboard-module-pearls-tile"
          stats={canViewValues ? [
            { label: 'Carats', value: canSeePearl ? formatCarats(stats.pearl_carats) : '—' },
            { label: 'Value', value: canSeePearl ? formatINR(stats.pearl_value) : '—' },
          ] : [
            { label: 'Carats', value: canSeePearl ? formatCarats(stats.pearl_carats) : '—' },
            { label: 'Pieces', value: canSeePearl ? (stats.pearl_pieces || 0) : '—' },
          ]}
        />
        <ModuleTile palette="goldsmith" icon={Hammer} title="Goldsmiths"
          subtitle="Orders & operations"
          to="/goldsmiths"
          testid="dashboard-module-goldsmiths-tile"
          stats={canViewValues ? [
            { label: 'Value', value: formatINR(stats.goldsmith_stock_value) },
            { label: 'Goldsmiths', value: `${stats.goldsmiths_with_balance} / ${stats.goldsmith_count}` },
          ] : [
            { label: 'Active', value: stats.goldsmiths_with_balance || 0 },
            { label: 'Goldsmiths', value: stats.goldsmith_count || 0 },
          ]}
        />
        <ModuleTile palette="jewellery" icon={Crown} title="Jewellery"
          subtitle="Finished pieces from goldsmiths"
          to="/jewellery"
          testid="dashboard-module-jewellery-tile"
          stats={canViewValues ? [
            { label: 'Pieces', value: stats.jewellery_count },
            { label: 'Value (at cost)', value: formatINR(stats.jewellery_value) },
          ] : [
            { label: 'Pieces', value: stats.jewellery_count },
          ]}
        />
      </div>

      {/* Wave 3.99p — Pending Requirements grouped by Order.
          Rendered whenever the operator has ANY material scope (so
          material-specific buyers plus Karigar / Order Manager / SA /
          CA all see it). The widget itself filters to the user's own
          scope; the backend enforces the same rule as a safety net. */}
      {effectiveCompanyId && (canSeeGold || canSeeDiamond || canSeeColorStone || canSeePearl) && (
        <div className="mt-6">
          <PendingRequirementsByOrderWidget companyId={effectiveCompanyId} refreshSignal={refreshNonce} />
        </div>
      )}

      {/* Recent activity + Delete/Attention tile — collapsed by default to
          declutter; user choice persisted in localStorage per tile.
          Wave 4.x — For admins the Requests tile has moved to the top beside
          Notifications, so here we show Recent Activity full-width. */}
      <div className={`mt-10 grid grid-cols-1 gap-6 ${isAdmin ? '' : 'lg:grid-cols-2'}`}>
        <RecentActivityWidget recentLogs={recentLogs} />
        {!isAdmin && <DeleteAndAttentionWidget isSuperAdmin={isSuperAdmin} refreshSignal={refreshNonce} />}
      </div>

      {/* Wave 3.78 — Production Timeline summary widget */}
      {effectiveCompanyId && (
        <div className="mt-6">
          <ProductionTimelineWidget companyId={effectiveCompanyId} refreshSignal={refreshNonce} />
        </div>
      )}

      {isSuperAdmin && (
        <div className="mt-6">
          <Card className="p-5">
            <div className="text-base font-medium text-[var(--ink-navy)] mb-1">Super Admin Quick Actions</div>
            <div className="text-xs text-slate-500 mb-4">Govern companies, users, and global rate definitions</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Link to="/admin/companies" data-testid="dashboard-admin-companies-link" className="flex items-center gap-2 px-3 py-3 rounded-lg border border-[var(--divider)] hover:bg-slate-50 text-sm">
                <Building2 size={14} /> Manage Companies
              </Link>
              <Link to="/admin/users" data-testid="dashboard-admin-users-link" className="flex items-center gap-2 px-3 py-3 rounded-lg border border-[var(--divider)] hover:bg-slate-50 text-sm">
                <Users size={14} /> Manage Users
              </Link>
              <Link to="/logs" data-testid="dashboard-logs-link" className="flex items-center gap-2 px-3 py-3 rounded-lg border border-[var(--divider)] hover:bg-slate-50 text-sm">
                <ScrollText size={14} /> System Logs
              </Link>
              <Link to="/reports" data-testid="dashboard-reports-link" className="flex items-center gap-2 px-3 py-3 rounded-lg border border-[var(--divider)] hover:bg-slate-50 text-sm">
                <AlertCircle size={14} /> Reports
              </Link>
            </div>
          </Card>
        </div>
      )}
    </AppShell>
  );
}

// ============================================================================
// Collapsible Recent Activity tile.
// Collapsed by default; user choice persisted in localStorage.
// ============================================================================
function useCollapsiblePref(key, defaultValue = false) {
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      if (v === '1') return true;
      if (v === '0') return false;
    } catch { /* noop */ }
    return defaultValue;
  });
  const setAndStore = (next) => {
    setOpen(next);
    try { localStorage.setItem(key, next ? '1' : '0'); } catch { /* noop */ }
  };
  return [open, setAndStore];
}

function RecentActivityWidget({ recentLogs }) {
  const [open, setOpen] = useCollapsiblePref('dash.recent_activity.open', false);
  return (
    <Card className="p-5" data-testid="dashboard-recent-activity">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-baseline justify-between gap-2 text-left"
        data-testid="dashboard-recent-activity-toggle"
      >
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)] flex items-center gap-2">
            <ScrollText size={14} /> Recent Activity
            {recentLogs.length > 0 && (
              <Badge className="bg-slate-100 text-slate-700 text-[10px]" data-testid="dashboard-recent-activity-count">
                {recentLogs.length}
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Latest events across your portal</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/logs"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-[var(--ink-navy)] hover:underline"
            data-testid="dashboard-recent-logs-view-all"
          >
            View all
          </Link>
          {open ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
        </div>
      </button>
      {open && (
        <div className="divide-y divide-[var(--divider)] mt-3" data-testid="dashboard-recent-activity-list">
          {recentLogs.length === 0 && <div className="py-6 text-center text-sm text-slate-500">No recent activity</div>}
          {recentLogs.map((l) => (
            <div key={l.id} className="py-2.5 flex items-start gap-3 text-sm" data-testid={`dashboard-recent-log-${l.id}`}>
              <Badge variant="secondary" className="text-[10px] uppercase tracking-wide mt-0.5">{l.action}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-slate-800 truncate">
                  <span className="text-slate-500">{l.user_email}</span>{' '}
                  <span className="font-mono text-xs text-slate-500">{l.entity_type}</span>{' '}
                  <span className="font-medium">{l.entity_label || ''}</span>
                </div>
              </div>
              <div className="text-[11px] text-slate-400 whitespace-nowrap">
                {new Date(l.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// Combined Delete Requests + Attention Requests tile (tabs)
//
// Collapsed by default. Header shows BOTH unread counts as badges so the
// SA sees pending work at a glance without expanding. Attention tab only
// visible to Super Admins (mirrors the Requests page tab).
// ============================================================================
function DeleteAndAttentionWidget({ isSuperAdmin, refreshSignal = 0 }) {
  const [open, setOpen] = useCollapsiblePref('dash.requests_tile.open', false);
  const [tab, setTab] = useState(isSuperAdmin ? 'delete' : 'delete');
  const [deleteList, setDeleteList] = useState([]);
  const [deleteCount, setDeleteCount] = useState(0);
  const [attentionList, setAttentionList] = useState([]);
  const [attentionCount, setAttentionCount] = useState(0);
  const [acking, setAcking] = useState({});

  const load = async () => {
    try {
      const [r, c] = await Promise.all([
        api.get('/delete-requests/', { params: { role_scope: 'awaiting_me' } }),
        api.get('/delete-requests/counts'),
      ]);
      setDeleteList(r.data.slice(0, 6));
      setDeleteCount(c.data?.awaiting_me || 0);
    } catch { /* noop */ }
    if (isSuperAdmin) {
      try {
        // Wave 3.44 — Server now filters out items the current SA has
        // already acknowledged, so no client-side trim required.
        const { data } = await api.get('/orders/sa-attention/pending');
        setAttentionList((data || []).slice(0, 6));
        setAttentionCount((data || []).length);
      } catch { /* noop */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isSuperAdmin, refreshSignal]);

  // Wave 3.44 — Refresh on window focus / tab visibility change so the
  // counts auto-clear after SA round-trips through Order Detail (which
  // already auto-acknowledges on view).
  useEffect(() => {
    const onFocus = () => load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  // Wave 3.44 — One-click manual acknowledgement (idempotent on the server).
  const acknowledge = async (commentId) => {
    if (acking[commentId]) return;
    setAcking((p) => ({ ...p, [commentId]: true }));
    try {
      await api.post(`/orders/sa-attention/${commentId}/acknowledge`);
      // Optimistic local update so the row vanishes immediately
      setAttentionList((prev) => prev.filter((c) => c.id !== commentId));
      setAttentionCount((n) => Math.max(0, n - 1));
    } catch {
      setAcking((p) => ({ ...p, [commentId]: false }));
    }
  };

  return (
    <Card className="p-5" data-testid="dashboard-requests-tile">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-baseline justify-between gap-2 text-left"
        data-testid="dashboard-requests-tile-toggle"
      >
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)] flex items-center gap-2 flex-wrap">
            <Inbox size={14} /> Requests
            {deleteCount > 0 && (
              <Badge className="bg-rose-50 text-rose-700 text-[10px]" data-testid="dashboard-requests-delete-count">
                {deleteCount} delete
              </Badge>
            )}
            {isSuperAdmin && attentionCount > 0 && (
              <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid="dashboard-requests-attention-count">
                {attentionCount} attention
              </Badge>
            )}
            {deleteCount === 0 && (!isSuperAdmin || attentionCount === 0) && (
              <Badge className="bg-emerald-50 text-emerald-700 text-[10px]" data-testid="dashboard-requests-empty-badge">
                All clear
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Items awaiting your action in the approval & escalation workflows</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/requests"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-[var(--ink-navy)] hover:underline"
            data-testid="dashboard-requests-view-all"
          >
            View all
          </Link>
          {open ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
        </div>
      </button>

      {open && (
        <div className="mt-3" data-testid="dashboard-requests-tile-body">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-white border border-[var(--divider)]">
              <TabsTrigger value="delete" data-testid="dashboard-tab-delete">
                <Inbox size={12} className="mr-1.5" /> Delete
                {deleteCount > 0 && <Badge className="ml-2 bg-rose-50 text-rose-700 text-[10px]">{deleteCount}</Badge>}
              </TabsTrigger>
              {isSuperAdmin && (
                <TabsTrigger value="attention" data-testid="dashboard-tab-attention">
                  <Shield size={12} className="mr-1.5" /> Attention
                  {attentionCount > 0 && <Badge className="ml-2 bg-amber-100 text-amber-800 text-[10px]">{attentionCount}</Badge>}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="delete" className="mt-3">
              {deleteList.length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-500" data-testid="dashboard-delete-empty">
                  Nothing waiting on you — all caught up.
                </div>
              ) : (
                <div className="divide-y divide-[var(--divider)]" data-testid="dashboard-delete-list">
                  {deleteList.map((r) => (
                    <Link key={r.id} to="/requests" className="block py-2.5 flex items-start gap-3 text-sm hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`dashboard-delete-row-${r.id}`}>
                      <Badge variant="secondary" className="text-[10px] uppercase tracking-wide mt-0.5">{(r.entity_type || '').replace('_', ' ')}</Badge>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-800 truncate"><span className="font-medium">{r.entity_label}</span></div>
                        <div className="text-[11px] text-slate-500 truncate">{r.requested_by_email}{r.reason ? ` — ${r.reason}` : ''}</div>
                      </div>
                      <div className="text-[11px] text-slate-400 whitespace-nowrap">{formatDateTime(r.requested_at)}</div>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            {isSuperAdmin && (
              <TabsContent value="attention" className="mt-3">
                {attentionList.length === 0 ? (
                  <div className="py-6 text-center text-sm text-slate-500" data-testid="dashboard-attention-empty">
                    No comments awaiting your acknowledgment.
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--divider)]" data-testid="dashboard-attention-list">
                    {attentionList.map((c) => (
                      <div key={c.id} className="py-2.5 flex items-start gap-3 text-sm hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`dashboard-attention-row-${c.id}`}>
                        <Link to={`/orders/${c.order_id}#comment-${c.id}`} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 flex items-start gap-3">
                          <Badge className="bg-amber-100 text-amber-800 text-[10px] mt-0.5">SA</Badge>
                          <div className="flex-1 min-w-0">
                            <div className="text-slate-800 truncate">
                              <span className="font-mono text-xs text-slate-500">Order {c.order_no}</span>{' '}
                              <span className="text-slate-700">{(c.body || '').slice(0, 100)}{(c.body || '').length > 100 ? '…' : ''}</span>
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">{c.created_by_name || c.created_by_email}</div>
                          </div>
                          <div className="text-[11px] text-slate-400 whitespace-nowrap">{formatDateTime(c.created_at)}</div>
                        </Link>
                        {/* Wave 3.44 — Acknowledge without opening the order. */}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); acknowledge(c.id); }}
                          disabled={!!acking[c.id]}
                          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-[11px] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                          title="Acknowledge this attention request"
                          data-testid={`dashboard-attention-ack-${c.id}`}
                        >
                          <Check size={11} />
                          {acking[c.id] ? 'Acking…' : 'Acknowledge'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}
    </Card>
  );
}


// ============================================================================
// Wave 3.78 — Production Timeline Widget (Dashboard)
// Counter row + Top-5 delayed orders + "View all" CTA.
// ============================================================================
function ProductionTimelineWidget({ companyId, refreshSignal = 0 }) {
  const [counters, setCounters] = useState({ active: 0, on_track: 0, at_risk: 0, delayed: 0, severe: 0, sla_off: 0 });
  const [topDelayed, setTopDelayed] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevCompanyRef = useRef(undefined);

  useEffect(() => {
    let cancelled = false;
    if (!companyId) return;
    // Wave 4.x — Show the skeleton only on first load / company switch; a
    // periodic refresh (refreshSignal) updates in place without flicker.
    const companyChanged = prevCompanyRef.current !== companyId;
    prevCompanyRef.current = companyId;
    const load = async () => {
      if (companyChanged) setLoading(true);
      try {
        const [countRes, listRes] = await Promise.all([
          api.get('/production-timeline/delayed-count', { params: { company_id: companyId } }),
          api.get('/production-timeline/orders', { params: { company_id: companyId, filter: 'delayed' } }),
        ]);
        if (!cancelled) {
          setCounters(countRes.data || {});
          const orders = (listRes.data?.orders || []).slice(0, 5);
          setTopDelayed(orders);
        }
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [companyId, refreshSignal]);

  return (
    <Card className="p-5" data-testid="dashboard-production-timeline-widget">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)] flex items-center gap-2">
            <Timer size={14} /> Production Timeline
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Every active order at a glance · delayed items first.</div>
        </div>
        <Link
          to="/production-timeline"
          className="text-xs text-[var(--ink-navy)] hover:text-[var(--gold-accent)] inline-flex items-center gap-1"
          data-testid="dashboard-production-timeline-view-all"
        >
          View all <ArrowRight size={12} />
        </Link>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
        <MiniCounter label="Active" value={counters.active} tone={null} testId="widget-counter-active" />
        <MiniCounter label="On Track" value={counters.on_track} tone="on_track" testId="widget-counter-on-track" />
        <MiniCounter label="At Risk" value={counters.at_risk} tone="at_risk" testId="widget-counter-at-risk" />
        <MiniCounter label="Delayed" value={counters.delayed} tone="delayed" testId="widget-counter-delayed" />
        <MiniCounter label="Critical" value={counters.severe} tone="severe" testId="widget-counter-severe" />
        {/* Wave 3.83 — SLA Off counter (clickable → filtered timeline) */}
        <Link to="/production-timeline?sla_off=1" className="focus-visible:outline-none" data-testid="widget-counter-sla-off-link">
          <MiniCounter label="SLA Off" value={counters.sla_off || 0} tone="sla_off" testId="widget-counter-sla-off" />
        </Link>
      </div>

      {/* Top delayed list */}
      {loading ? (
        <div className="text-xs text-slate-500 py-2">Loading…</div>
      ) : topDelayed.length === 0 ? (
        <div className="text-xs text-slate-500 py-2" data-testid="widget-no-delayed">
          🎉 Nothing is delayed right now.
        </div>
      ) : (
        <div className="space-y-1.5">
          {topDelayed.map((o) => (
            <Link
              key={o.order_id}
              to={`/orders/${o.order_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-slate-50 border border-transparent hover:border-[var(--divider)]"
              data-testid={`widget-delayed-row-${o.order_no}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-mono tabular-nums text-[var(--ink-navy)] truncate">{o.order_no}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {o.customer_name || '—'} · {o.current_stage_label}
                </div>
                {o.requirements_progress && o.requirements_progress.total > 0
                  && (o.current_stage_kind === 'fulfilling_requirements'
                    || (o.blocked_stages || []).some((b) => b.blocked_by === 'fulfilling_requirements')) && (
                  <div className="text-[10px] mt-0.5">
                    <span className="inline-flex items-center gap-1 font-mono tabular-nums px-1 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800">
                      {o.requirements_progress.completed}/{o.requirements_progress.total} reqs
                    </span>
                  </div>
                )}
              </div>
              <div className="text-[11px] font-mono tabular-nums shrink-0" style={{ color: DELAY_COLORS.delayed.text }}>
                +{formatHours(o.delayed_by_hours)}
              </div>
              <DelayBadge order={o} size="sm" />
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}

function MiniCounter({ label, value, tone, testId }) {
  const c = tone && DELAY_COLORS[tone];
  return (
    <div
      className="p-2.5 rounded-md border"
      style={c ? { borderColor: c.border, background: c.bg + '55' } : { borderColor: 'var(--divider)' }}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div
        className="text-lg font-medium font-mono tabular-nums leading-tight mt-0.5"
        style={c ? { color: c.text } : { color: 'var(--ink-navy)' }}
      >
        {value ?? 0}
      </div>
    </div>
  );
}


// ============================================================================
// Wave 3.99p → 3.99v — Pending Requirements grouped by Order widget.
//
// Wave 3.99v additions (this file):
//   • **Cascading filters** — every chip dimension (Search, Goldsmith,
//     Customer, Stage, Material, Age, Jewellery) computes its option
//     list AND its option counts against the CURRENT filter combo,
//     minus its own dimension. That means selecting a goldsmith
//     immediately trims the Customer dropdown to customers who work
//     with that goldsmith, and vice-versa across every dimension.
//   • **Type of Jewellery** chip filter (plain-text jewellery_type
//     stamped on the order, e.g. Ring / Necklace).
//   • **Order thumbnail** rendered beside the order no. Uses the
//     first ``images_thumb`` entry from the order document — a small
//     JPEG that keeps the widget snappy.
//   • **Grid ↔ Table view toggle** (preference persisted per user so
//     buyers who prefer a compact spreadsheet land back on the same
//     view every login).
//
// Prior waves recap:
//   • 3.99p — Widget scaffolding + per-material grouping.
//   • 3.99r — Age chips, per-order goldsmith name, pending X/Y count.
//   • 3.99t — Multi-select chip filter bar + Combined PDF that
//     honours the on-screen chip filters + auto-disabled button.
// ============================================================================
const _AGE_BUCKETS = [
  { id: 'today', label: 'Today', min: 0, max: 0 },
  { id: '1-3',   label: '1-3 days', min: 1, max: 3 },
  { id: '4-7',   label: '4-7 days', min: 4, max: 7 },
  { id: '8-14',  label: '8-14 days', min: 8, max: 14 },
  { id: '15+',   label: '15+ days', min: 15, max: null },
];

const _MAT_LABELS = {
  gold: 'Gold', diamond: 'Diamonds', color_stone: 'Colour Stones', pearl: 'Pearls',
};

// Wave 4.10e — Raw Material buyer-tracking statuses (side materials only).
const _RM_STATUS_DEFS = [
  { id: 'order_pending', label: 'Order Pending' },
  { id: 'order_raised', label: 'Order Raised' },
  { id: 'material_collected', label: 'Material Collected' },
];
const _RM_STATUS_LABELS = Object.fromEntries(_RM_STATUS_DEFS.map((d) => [d.id, d.label]));

// ---------------------------------------------------------------------------
// Wave 3.99w — Canonical Stage options mirrored from the Order Forms
// page. Matches the exact grouped list rendered inside the Orders
// Stage <Select>: legacy top-level order stages first, then the
// Production Checklist step keys the checklist DAG emits.
// ---------------------------------------------------------------------------
const _STAGE_LEGACY = [
  { id: 'Issued',        label: 'Issued' },
  { id: 'In Production', label: 'In Production' },
  { id: 'Quality Check', label: 'Quality Check' },
  { id: 'Received Back', label: 'Received Back' },
  { id: 'Completed',     label: 'Completed' },
  { id: 'Cancelled',     label: 'Cancelled' },
];
const _STAGE_STEPS = [
  { id: 'cad',          label: 'CAD Active' },
  { id: 'cam',          label: 'CAM Active' },
  { id: 'chamka',       label: 'Chamka Active' },
  { id: 'ghat_pending_gold_order', label: 'Ghat - Pending Gold Order' },
  { id: 'ghat_waiting', label: 'Ghat - Waiting For Gold' },
  { id: 'ghat',         label: 'Ghat Active' },
  { id: 'raw_material', label: 'Raw Material Active' },
  { id: 'setting',      label: 'Setting Active' },
  { id: 'qc',           label: 'Quality Check Active' },
  { id: 'delivery',     label: 'Delivery Active' },
];

// ---------------------------------------------------------------------------
// Cascading-filter engine.
// ---------------------------------------------------------------------------
// ``applyFilters`` is the single source of truth used by:
//   * ``filteredOrders``           (skipDim = null)
//   * option/count computations    (skipDim = 'goldsmith' etc.,
//                                   overrideValue = candidate value)
//   * chip contextual counts       (skipDim = the chip's dimension,
//                                   overrideValue = the chip's value)
//
// Rationale: keeping one function for everything guarantees the chip
// count you see and the row count you get after clicking that chip
// stay perfectly in sync.
//
// ``skipDim`` — the dimension name being probed. That dimension's
// active values are ignored (the caller supplies ``overrideValue`` if
// it wants to test a specific candidate — the standard faceted-
// search cascade pattern).
// ---------------------------------------------------------------------------
function _rowMatchesAgeBucket(row, bucketIds) {
  if (!bucketIds || bucketIds.length === 0) return true;
  const age = Number(row.age_days || 0);
  return bucketIds.some((bid) => {
    const b = _AGE_BUCKETS.find((x) => x.id === bid);
    if (!b) return false;
    if (age < b.min) return false;
    if (b.max !== null && age > b.max) return false;
    return true;
  });
}

function _applyFilters(order, state, opts = {}) {
  const skipDim = opts.skipDim || null;
  const overrideValue = opts.overrideValue;

  // ------------- Search (order_no + title) -------------
  if (skipDim !== 'search') {
    const q = (state.search || '').trim().toLowerCase();
    if (q) {
      const hay = [order.order_no, order.title].map((x) => (x || '').toLowerCase()).join(' | ');
      if (!hay.includes(q)) return null;
    }
  } else if (overrideValue) {
    const hay = [order.order_no, order.title].map((x) => (x || '').toLowerCase()).join(' | ');
    if (!hay.includes(String(overrideValue).toLowerCase())) return null;
  }

  // ------------- Goldsmith -------------
  const gs = skipDim === 'goldsmith'
    ? (overrideValue !== undefined ? [overrideValue] : [])
    : (state.goldsmiths || []);
  if (gs.length && !gs.includes(order.goldsmith_name || '')) return null;

  // ------------- Customer -------------
  const cs = skipDim === 'customer'
    ? (overrideValue !== undefined ? [overrideValue] : [])
    : (state.customers || []);
  if (cs.length && !cs.includes(order.customer_name || '')) return null;

  // ------------- Order (order_no + title) -------------
  // Wave 4.02 — Order-level multi-select. Values are order_id strings so
  // duplicate order numbers/titles never collide.
  const ords = skipDim === 'order'
    ? (overrideValue !== undefined ? [overrideValue] : [])
    : (state.orders || []);
  if (ords.length && !ords.includes(order.order_id)) return null;

  // ------------- Reference No. -------------
  // Reference-Number system — multi-select over the order's reference_no.
  const refs = skipDim === 'reference'
    ? (overrideValue !== undefined ? [overrideValue] : [])
    : (state.references || []);
  if (refs.length && !refs.includes(order.reference_no)) return null;

  // ------------- Stage -------------
  // Wave 3.99w — Stage semantic now covers BOTH the top-level order
  // stage AND the currently-active Production Checklist step ids so
  // buyers can filter "orders in CAD Active" the same way the Orders
  // page does. Any candidate value in the ``stages`` array is a match
  // if it equals ``order.stage`` OR appears in ``order.active_steps``.
  const st = skipDim === 'stage'
    ? (overrideValue !== undefined ? [overrideValue] : [])
    : (state.stages || []);
  if (st.length) {
    const orderStage = order.stage || '';
    const activeSteps = Array.isArray(order.active_steps) ? order.active_steps : [];
    const stagePass = st.some((v) => v === orderStage || activeSteps.includes(v));
    if (!stagePass) return null;
  }

  // ------------- Jewellery type -------------
  const jt = skipDim === 'jewellery'
    ? (overrideValue !== undefined ? [overrideValue] : [])
    : (state.jewelleryTypes || []);
  if (jt.length && !jt.includes(order.jewellery_type || '')) return null;

  // ------------- Material (per-row) -------------
  const mats = skipDim === 'material'
    ? (overrideValue !== undefined ? [overrideValue] : [])
    : (state.materials || []);
  // ------------- Raw Material buyer status (per-material) -------------
  // Wave 4.10e — Order Pending / Order Raised / Material Collected. Only side
  // materials (diamond/color_stone/pearl) carry a buyer status; gold is
  // excluded whenever this filter is active.
  const rmStatuses = skipDim === 'rmStatus'
    ? (overrideValue !== undefined ? [overrideValue] : [])
    : (state.rmStatus || []);
  // ------------- Age bucket (per-row) -------------
  const abs = skipDim === 'age'
    ? (overrideValue !== undefined ? [overrideValue] : [])
    : (state.ageBuckets || []);

  const newGroups = {};
  let kept = 0;
  for (const [matKey, rows] of Object.entries(order.groups || {})) {
    if (mats.length && !mats.includes(matKey)) continue;
    // Raw-material buyer-status gate: keep only material groups whose
    // procurement state matches the selection (gold has none → dropped).
    if (rmStatuses.length) {
      const bs = (order.rm_buyer_status || {})[matKey];
      if (!bs || !rmStatuses.includes(bs)) continue;
    }
    const rowsKept = rows.filter((r) => _rowMatchesAgeBucket(r, abs));
    if (rowsKept.length) {
      // Wave 3.99w — Defensive re-sort so the Diamond hierarchy is
      // preserved even if age-bucket filtering drops rows out of
      // order (or the payload is served by a pre-3.99w backend).
      newGroups[matKey] = matKey === 'diamond' ? _sortDiamondItems(rowsKept) : rowsKept;
      kept += rowsKept.length;
    }
  }
  if (kept === 0) return null;
  return { ...order, groups: newGroups, pending_count: kept };
}

function _pendingCount(order) {
  return Object.values(order.groups || {}).reduce((s, rows) => s + (rows?.length || 0), 0);
}

// Widget-scoped chip in the filter bar. Every chip shows its label +
// contextual count + × removal so buyers can dismantle their filter
// combo one dimension at a time.
function FilterChip({ label, count, onRemove, testId }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-800 border border-violet-100 px-2 py-0.5 text-[11px] leading-none max-w-full"
      data-testid={testId}
    >
      <span className="truncate">{label}</span>
      {typeof count === 'number' && (
        <span className="text-violet-500 font-mono tabular-nums">({count})</span>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 text-violet-500 hover:text-violet-800 shrink-0"
        aria-label="Remove filter"
        data-testid={`${testId}-remove`}
      >
        <X size={11} />
      </button>
    </span>
  );
}

// Multi-select popover used inside the "Add filter" menu. Cascades:
// options and their counts arrive pre-computed by the caller so we
// stay a dumb presentation component.
//
// Wave 3.99w — Optional ``groups`` prop lets the caller partition the
// option list into labelled sections (e.g. "Order Stage" vs
// "Production Checklist Step") the same way the Orders page's Stage
// Select dropdown does.
function MultiSelectPopover({
  triggerLabel, options, selected, onChange, testId, placeholder, groups,
}) {
  const [openMs, setOpenMs] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [options, query]);
  const selectedSet = new Set(selected);
  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  const clearAll = () => onChange([]);

  // When ``groups`` is supplied, partition the filtered list by
  // ``groups[i].ids`` so we can render section headers between them.
  const grouped = useMemo(() => {
    if (!Array.isArray(groups) || groups.length === 0) return null;
    const seen = new Set();
    const sections = groups.map((g) => {
      const idsSet = new Set(g.ids || []);
      const items = filtered.filter((o) => {
        if (!idsSet.has(o.id)) return false;
        seen.add(o.id);
        return true;
      });
      return { label: g.label, items };
    });
    // Anything not claimed by any explicit group goes into an "Other"
    // trailing section so nothing is silently dropped.
    const rest = filtered.filter((o) => !seen.has(o.id));
    if (rest.length) sections.push({ label: 'Other', items: rest });
    return sections;
  }, [groups, filtered]);

  const renderRow = (opt) => {
    const active = selectedSet.has(opt.id);
    return (
      <button
        key={opt.id}
        type="button"
        onClick={() => toggle(opt.id)}
        className="w-full text-left flex items-start gap-2 px-2 py-1 rounded hover:bg-slate-50"
        data-testid={`${testId}-option-${opt.id}`}
      >
        <Checkbox checked={active} onCheckedChange={() => toggle(opt.id)} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] text-slate-800 truncate">{opt.label}</div>
          {typeof opt.count === 'number' && (
            <div className="text-[10px] text-slate-400">{opt.count} pending</div>
          )}
        </div>
      </button>
    );
  };

  return (
    <Popover open={openMs} onOpenChange={setOpenMs}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50 hover:border-slate-400"
          data-testid={`${testId}-trigger`}
        >
          <Plus size={11} /> {triggerLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start" data-testid={`${testId}-content`}>
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder || 'Search…'}
              className="h-7 pl-7 text-[11px]"
              data-testid={`${testId}-search`}
            />
          </div>
          {selectedSet.size > 0 && (
            <div className="flex items-center justify-between mt-1.5 text-[10px]">
              <span className="text-slate-500">{selectedSet.size} selected</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-violet-700 hover:underline"
                data-testid={`${testId}-clear`}
              >
                Clear
              </button>
            </div>
          )}
        </div>
        <div className="max-h-56 overflow-y-auto p-1">
          {filtered.length === 0 && (
            <div className="text-[11px] text-slate-500 px-2 py-3">No matches.</div>
          )}
          {grouped ? (
            grouped.map((section, idx) => (
              section.items.length > 0 && (
                <div key={`sec-${idx}`}>
                  <div className="px-2 pt-1.5 pb-0.5 text-[9.5px] uppercase tracking-wider text-slate-400 font-semibold">
                    {section.label}
                  </div>
                  {section.items.map(renderRow)}
                </div>
              )
            ))
          ) : (
            filtered.map(renderRow)
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Small square thumbnail beside the order no. Falls back to a subtle
// placeholder icon when the order has no image so grid rows stay
// visually balanced.
function OrderThumb({ url, size = 32, testId }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        loading="lazy"
        className="rounded-md object-cover border border-slate-200 shrink-0 bg-slate-50"
        style={{ width: size, height: size }}
        data-testid={testId}
      />
    );
  }
  return (
    <div
      className="rounded-md border border-dashed border-slate-200 bg-slate-50 shrink-0 flex items-center justify-center text-slate-300"
      style={{ width: size, height: size }}
      data-testid={testId}
      aria-hidden="true"
    >
      <ImageIcon size={size * 0.45} />
    </div>
  );
}

function PendingRequirementsByOrderWidget({ companyId, hideExport = false, refreshSignal = 0 }) {
  const [open, setOpen] = useCollapsiblePref('dashboard.pending-requirements.open', true);
  // Wave 3.99v / Wave 4.06 — Grid/Table view preference. Default is now
  // **Table** for everyone: we bumped the persistence key to a ``.v2`` so
  // any older ``grid`` preference is superseded and all users land on the
  // table view. Subsequent toggles still persist per browser.
  const [view, setView] = useState(() => {
    try {
      const v = localStorage.getItem('dashboard.pending-requirements.view.v2');
      return v === 'grid' ? 'grid' : 'table';
    } catch { return 'table'; }
  });
  const setViewPersisted = (next) => {
    setView(next);
    try { localStorage.setItem('dashboard.pending-requirements.view.v2', next); } catch { /* noop */ }
  };
  // Wave 4.04 — Remembered "new page per Page No / Order No" preference for
  // the combined PDF export. Persisted so operators keep their choice.
  const [pageBreakPref, setPageBreakPref] = useState(() => {
    try { return localStorage.getItem('dashboard.pending-requirements.pagebreak') === '1'; } catch { return false; }
  });
  const setPageBreakPersisted = (next) => {
    setPageBreakPref(next);
    try { localStorage.setItem('dashboard.pending-requirements.pagebreak', next ? '1' : '0'); } catch { /* noop */ }
  };
  // Sort toggle — order by Order No (newest / oldest). Newest first by
  // default; remembered across sessions. Controls the on-screen order
  // (within each Page No group) AND the combined PDF export.
  const [sortDir, setSortDir] = useState(() => {
    try { return localStorage.getItem('dashboard.pending-requirements.sort') === 'oldest' ? 'oldest' : 'newest'; } catch { return 'newest'; }
  });
  const setSortPersisted = (next) => {
    setSortDir(next);
    try { localStorage.setItem('dashboard.pending-requirements.sort', next); } catch { /* noop */ }
  };
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState({ orders: [], material_scope: [], total_pending: 0 });
  // Wave 4.x — Progressive loading (mirrors Orders / Goldsmith Detail):
  // fast first page (15) renders instantly, a cheap ``count_only`` request
  // fills the totals + tab count, then the full set streams in the
  // background so client-side filters + tabs 2+ see everything.
  const loadSeqRef = React.useRef(0);
  const [fullyLoaded, setFullyLoaded] = useState(false);
  const [serverCounts, setServerCounts] = useState({ order_count: null, total_pending: null });

  // Multi-dimension filter state. Each dimension is an array of
  // primitive values so we can multi-select across dimensions and
  // cascade cleanly.
  const [search, setSearch] = useState('');
  const [goldsmiths, setGoldsmiths] = useState([]);
  const [customersSel, setCustomersSel] = useState([]);
  const [orderSel, setOrderSel] = useState([]);
  const [stages, setStages] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [ageBuckets, setAgeBuckets] = useState([]);
  const [jewelleryTypes, setJewelleryTypes] = useState([]);
  // Reference-Number system — enabled per company + multi-select filter.
  const { enabled: refEnabled, label: refLabel, labelShort: refLabelShort } = useReferenceConfig(companyId);
  const [referenceSel, setReferenceSel] = useState([]);
  // Wave 4.10e — Raw Material buyer status multi-select (Order Pending /
  // Order Raised / Material Collected).
  const [rmStatus, setRmStatus] = useState([]);

  // Handy struct passed into _applyFilters so we're not shuttling 7
  // arguments around.
  const filterState = useMemo(() => ({
    search, goldsmiths, customers: customersSel, orders: orderSel, stages, materials, ageBuckets, jewelleryTypes, references: referenceSel, rmStatus,
  }), [search, goldsmiths, customersSel, orderSel, stages, materials, ageBuckets, jewelleryTypes, referenceSel, rmStatus]);

  const prevCompanyRef = React.useRef(undefined);
  useEffect(() => {
    let cancelled = false;
    // Wave 4.x — Only reset to the skeleton on first load / company switch.
    // A periodic refresh (refreshSignal) updates the list in place.
    const companyChanged = prevCompanyRef.current !== companyId;
    prevCompanyRef.current = companyId;
    async function fetchData() {
      if (!companyId) return;
      const seq = ++loadSeqRef.current;
      if (companyChanged) {
        setLoading(true);
        setFullyLoaded(false);
      }
      const empty = { orders: [], material_scope: [], total_pending: 0 };
      // (a) cheap totals — fills the header count + pagination tab count up front.
      api.get('/dashboard/pending-requirements', { params: { company_id: companyId, count_only: true } })
        .then(({ data: c }) => {
          if (!cancelled && seq === loadSeqRef.current) {
            setServerCounts({
              order_count: typeof c?.order_count === 'number' ? c.order_count : null,
              total_pending: typeof c?.total_pending === 'number' ? c.total_pending : null,
            });
          }
        })
        .catch(() => { if (!cancelled && seq === loadSeqRef.current) setServerCounts({ order_count: null, total_pending: null }); });
      // (b) fast first page (15 orders) so the first range tab renders instantly.
      //     Skipped on a silent refresh — we already have the full set, so we
      //     avoid briefly shrinking the list to 15 rows before (c) returns.
      if (companyChanged) {
        try {
          const { data: payload } = await api.get('/dashboard/pending-requirements', {
            params: { company_id: companyId, limit: 15 },
          });
          if (!cancelled && seq === loadSeqRef.current) {
            setData(payload || empty);
            setLoading(false);
          }
        } catch {
          if (!cancelled && seq === loadSeqRef.current) { setData(empty); setLoading(false); }
        }
      }
      // (c) full set in the background — powers tabs 2+ and client-side filters.
      api.get('/dashboard/pending-requirements', { params: { company_id: companyId } })
        .then(({ data: payload }) => {
          if (cancelled || seq !== loadSeqRef.current) return;
          setData(payload || empty);
          setFullyLoaded(true);
          setLoading(false);
        })
        .catch(() => { if (!cancelled && seq === loadSeqRef.current) { setFullyLoaded(true); setLoading(false); } });
    }
    fetchData();
    return () => { cancelled = true; };
  }, [companyId, refreshSignal]);

  // Stage + Material chips only make sense when the material scope
  // spans 2+ materials — single-material buyers get a leaner chip set.
  const showStageMaterialFilters = (data.material_scope || []).length >= 2;

  // -----------------------------------------------------------------
  // Filtered view — order-level + requirement-level filters combined.
  // -----------------------------------------------------------------
  const filteredOrders = useMemo(() => {
    const out = [];
    for (const o of (data.orders || [])) {
      const kept = _applyFilters(o, filterState);
      if (kept) out.push(kept);
    }
    return out;
  }, [data.orders, filterState]);

  const filteredPendingCount = filteredOrders.reduce((s, o) => s + (o.pending_count || 0), 0);

  // Wave 4.x — While the full set is still streaming in the background we
  // prefer the cheap ``count_only`` totals so the header shows the true
  // numbers instead of just the first-page (15) figures.
  const displayTotalPending = (!fullyLoaded && serverCounts.total_pending != null)
    ? serverCounts.total_pending
    : data.total_pending;
  const displayOrderCount = (!fullyLoaded && serverCounts.order_count != null)
    ? serverCounts.order_count
    : (data.orders || []).length;

  // When a filter is active: distinct running-numbers among the orders that
  // still have pending requirements (powers the "x <term>s" header chip).
  const filteredDistinctReferenceCount = (() => {
    if (!refEnabled) return 0;
    const set = new Set();
    for (const o of filteredOrders) {
      if (o.reference_no != null) set.add(o.reference_no);
    }
    return set.size;
  })();

  // -----------------------------------------------------------------
  // Grouping — when the company uses running-numbers we group orders
  // by their reference/page number (ascending), and WITHIN each group
  // sort orders by order_no. When the feature is off we fall back to a
  // single flat group so the historical "by order" layout is preserved.
  // -----------------------------------------------------------------
  const refTerm = refLabel.replace(/\.$/, '');
  const buildGroups = (list) => {
    // Honour the sort toggle: newest = highest Order No first (descending).
    const dirMul = sortDir === 'oldest' ? 1 : -1;
    const byOrderNo = (a, b) =>
      dirMul * (a.order_no || '').localeCompare(b.order_no || '', undefined, { numeric: true, sensitivity: 'base' });
    if (!refEnabled) {
      return [{ ref: null, label: null, orders: [...list].sort(byOrderNo) }];
    }
    const map = new Map();
    for (const o of list) {
      const key = o.reference_no != null ? o.reference_no : '__none__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    }
    const groups = [];
    for (const [key, ords] of map.entries()) {
      ords.sort(byOrderNo);
      groups.push({
        ref: key === '__none__' ? null : key,
        label: key === '__none__' ? `No ${refTerm}` : `${refTerm} ${key}`,
        orders: ords,
      });
    }
    groups.sort((a, b) => {
      // Page-No-less groups always trail, regardless of direction.
      if (a.ref == null && b.ref == null) return 0;
      if (a.ref == null) return 1;
      if (b.ref == null) return -1;
      // Direction: newest = highest Page No first (descending); oldest asc.
      const na = Number(a.ref); const nb = Number(b.ref);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return dirMul * (na - nb);
      return dirMul * String(a.ref).localeCompare(String(b.ref), undefined, { numeric: true });
    });
    return groups;
  };

  // Wave 4.x — Display-ordered FLAT list of the filtered orders (the same
  // order they'd render in, grouped-by-reference then by order_no). This
  // drives the 15-per-tab pagination below.
  const orderedFlat = useMemo(
    () => buildGroups(filteredOrders).flatMap((g) => g.orders),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredOrders, refEnabled, refTerm, sortDir],
  );

  // Wave 4.x — Pagination: show only 15 orders per range tab (1-15, 16-30,
  // 31-45, …). Filters/search still run over the FULL ``filteredOrders``
  // set; only the active range is grouped + rendered.
  const PENDING_PAGE_SIZE = 15;
  const [pendingPage, setPendingPage] = useState(0); // 0-indexed range
  useEffect(() => { setPendingPage(0); }, [orderedFlat.length]);
  const pendingTotalOrders = orderedFlat.length;
  const pendingTotalPages = Math.max(1, Math.ceil(pendingTotalOrders / PENDING_PAGE_SIZE));
  const pendingCurrent = Math.min(pendingPage, pendingTotalPages - 1);
  const pendingStart = pendingCurrent * PENDING_PAGE_SIZE;
  const pagedFlat = orderedFlat.slice(pendingStart, pendingStart + PENDING_PAGE_SIZE);

  // The render loops over ``referenceGroups`` — now the current page's orders
  // re-grouped so the reference headers stay intact within the visible range.
  const referenceGroups = useMemo(
    () => buildGroups(pagedFlat),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pagedFlat, refEnabled, refTerm, sortDir],
  );

  // -----------------------------------------------------------------
  // Cascading option computation. For each dimension we probe the
  // dataset with THAT dimension skipped, then bucket the surviving
  // orders/rows by candidate value. This is the standard faceted-
  // search count semantic: "if I toggled this value ON, how many
  // items would match given all the OTHER active filters?"
  // -----------------------------------------------------------------
  const _cascade = (skipDim, extractCandidatesFromOrder) => {
    // Base pool: orders passing all OTHER filters. We build this
    // once by calling _applyFilters with skipDim + no overrideValue
    // (i.e. no restriction on the skipped dimension).
    const pool = [];
    for (const o of (data.orders || [])) {
      const kept = _applyFilters(o, filterState, { skipDim });
      if (kept) pool.push(kept);
    }
    // For each candidate value in the pool, count how many pending
    // rows would remain if that value were the ONLY value selected
    // in the skipped dimension.
    const counts = new Map();
    for (const o of pool) {
      const cands = extractCandidatesFromOrder(o) || [];
      const seenForOrder = new Set();
      for (const cand of cands) {
        if (seenForOrder.has(cand)) continue;
        seenForOrder.add(cand);
        // Rebuild the group to isolate rows relevant to this cand
        // when skipDim is a row-level dimension.
        const kept = _applyFilters(o, filterState, { skipDim, overrideValue: cand });
        if (kept) counts.set(cand, (counts.get(cand) || 0) + kept.pending_count);
      }
    }
    return counts;
  };

  const goldsmithCounts = useMemo(
    () => _cascade('goldsmith', (o) => (o.goldsmith_name ? [o.goldsmith_name] : [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.orders, filterState],
  );
  const customerCounts = useMemo(
    () => _cascade('customer', (o) => (o.customer_name ? [o.customer_name] : [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.orders, filterState],
  );
  const orderCounts = useMemo(
    () => _cascade('order', (o) => (o.order_id ? [o.order_id] : [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.orders, filterState],
  );
  const referenceCounts = useMemo(
    () => _cascade('reference', (o) => (o.reference_no != null ? [o.reference_no] : [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.orders, filterState],
  );
  // order_id -> "OrderNo — Title" label for the Order multi-select.
  const orderLabelById = useMemo(() => {
    const m = {};
    for (const o of (data.orders || [])) {
      if (!o.order_id) continue;
      m[o.order_id] = o.title ? `${o.order_no} — ${o.title}` : (o.order_no || o.order_id);
    }
    return m;
  }, [data.orders]);
  const stageCounts = useMemo(
    () => _cascade('stage', (o) => {
      // Wave 3.99w — Contribute BOTH the top-level order stage AND
      // every currently-active checklist step id so the Stage chip
      // popover surfaces counts against the exact same option set
      // the Orders page uses.
      const out = [];
      if (o.stage) out.push(o.stage);
      if (Array.isArray(o.active_steps)) o.active_steps.forEach((s) => { if (s) out.push(s); });
      return out;
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.orders, filterState],
  );
  const jewelleryCounts = useMemo(
    () => _cascade('jewellery', (o) => (o.jewellery_type ? [o.jewellery_type] : [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.orders, filterState],
  );
  const materialCounts = useMemo(
    () => _cascade('material', (o) => Object.keys(o.groups || {})),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.orders, filterState],
  );
  // Wave 4.10e — Raw Material buyer-status cascade counts (Order Pending /
  // Order Raised / Material Collected across the order's pending side materials).
  const rmStatusCounts = useMemo(
    () => _cascade('rmStatus', (o) => {
      const vals = Object.values(o.rm_buyer_status || {});
      return Array.from(new Set(vals.filter((v) => v && v !== 'issued')));
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.orders, filterState],
  );
  const ageBucketCounts = useMemo(
    () => _cascade('age', (o) => {
      const found = new Set();
      Object.values(o.groups || {}).forEach((rows) => {
        (rows || []).forEach((r) => {
          const age = Number(r.age_days || 0);
          const b = _AGE_BUCKETS.find((x) => age >= x.min && (x.max === null || age <= x.max));
          if (b) found.add(b.id);
        });
      });
      return Array.from(found);
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.orders, filterState],
  );

  // Build sorted option arrays for each popover. Selected values are
  // always kept in the list (even if their cascaded count is 0) so
  // buyers can always uncheck them.
  const _buildOptions = (counts, selected, mapper) => {
    const keys = new Set([...counts.keys(), ...(selected || [])]);
    return Array.from(keys)
      .map((k) => mapper(k, counts.get(k) || 0))
      .sort((a, b) => (a.sortKey || a.label).localeCompare(b.sortKey || b.label));
  };

  const goldsmithOptions = useMemo(
    () => _buildOptions(goldsmithCounts, goldsmiths, (k, c) => ({ id: k, label: k, count: c })),
    [goldsmithCounts, goldsmiths],
  );
  const customerOptions = useMemo(
    () => _buildOptions(customerCounts, customersSel, (k, c) => ({ id: k, label: k, count: c })),
    [customerCounts, customersSel],
  );
  const orderOptions = useMemo(
    () => _buildOptions(
      orderCounts,
      orderSel,
      (k, c) => ({ id: k, label: orderLabelById[k] || k, count: c }),
    ),
    [orderCounts, orderSel, orderLabelById],
  );
  const referenceOptions = useMemo(
    () => _buildOptions(
      referenceCounts,
      referenceSel,
      (k, c) => ({ id: k, label: String(k), count: c, sortKey: String(k).padStart(12, '0') }),
    ),
    [referenceCounts, referenceSel],
  );
  const stageOptions = useMemo(() => {
    // Wave 3.99w — Build a canonical, grouped Stage option list that
    // matches the Orders page Select dropdown. Only include options
    // that either (a) match a stage/step currently present in the
    // cascaded data OR (b) are already selected (so the chip can be
    // unchecked). Everything else is hidden to keep the popover
    // tight.
    const canonical = [..._STAGE_LEGACY, ..._STAGE_STEPS];
    const canonicalLabelById = Object.fromEntries(canonical.map((s) => [s.id, s.label]));
    const stageKeys = new Set([
      ...stageCounts.keys(),
      ...stages,
    ]);
    const items = canonical
      .filter((s) => stageKeys.has(s.id))
      .map((s) => ({ id: s.id, label: s.label, count: stageCounts.get(s.id) || 0 }));
    // Preserve any pre-existing custom stages that aren't in the
    // canonical taxonomy (e.g. legacy orders with a bespoke stage
    // string) so operators never lose visibility of them.
    stageCounts.forEach((count, key) => {
      if (canonicalLabelById[key]) return;
      items.push({ id: key, label: key, count });
    });
    return items;
  }, [stageCounts, stages]);
  // Wave 3.99w — Group descriptors passed to the Stage popover so
  // section headers ("Order Stage" / "Production Checklist Step")
  // render inline.
  const stageGroups = useMemo(() => ([
    { label: 'Order Stage', ids: _STAGE_LEGACY.map((s) => s.id) },
    { label: 'Production Checklist Step', ids: _STAGE_STEPS.map((s) => s.id) },
  ]), []);
  const jewelleryOptions = useMemo(
    () => _buildOptions(jewelleryCounts, jewelleryTypes, (k, c) => ({ id: k, label: k, count: c })),
    [jewelleryCounts, jewelleryTypes],
  );
  const materialOptions = useMemo(() => {
    const ORDER = ['gold', 'diamond', 'color_stone', 'pearl'];
    const keys = new Set([...materialCounts.keys(), ...materials]);
    return ORDER
      .filter((k) => keys.has(k))
      .map((k) => ({ id: k, label: _MAT_LABELS[k] || k, count: materialCounts.get(k) || 0 }));
  }, [materialCounts, materials]);
  const rmStatusOptions = useMemo(() => {
    const keys = new Set([...rmStatusCounts.keys(), ...rmStatus]);
    return _RM_STATUS_DEFS
      .filter((d) => keys.has(d.id))
      .map((d) => ({ id: d.id, label: d.label, count: rmStatusCounts.get(d.id) || 0 }));
  }, [rmStatusCounts, rmStatus]);
  const ageBucketOptions = useMemo(() => {
    const keys = new Set([...ageBucketCounts.keys(), ...ageBuckets]);
    return _AGE_BUCKETS
      .filter((b) => keys.has(b.id))
      .map((b) => ({ id: b.id, label: b.label, count: ageBucketCounts.get(b.id) || 0 }));
  }, [ageBucketCounts, ageBuckets]);

  // Cascaded search chip count — how many rows the current search
  // term contributes to the current filter combo.
  const searchMatchCount = useMemo(() => {
    if (!(search || '').trim()) return 0;
    let c = 0;
    for (const o of (data.orders || [])) {
      const kept = _applyFilters(o, filterState, { skipDim: 'search', overrideValue: search });
      if (kept) c += kept.pending_count;
    }
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.orders, filterState]);

  const activeChipCount =
    (search ? 1 : 0) + goldsmiths.length + customersSel.length + orderSel.length + stages.length +
    materials.length + ageBuckets.length + jewelleryTypes.length + referenceSel.length + rmStatus.length;

  const clearAll = () => {
    setSearch('');
    setGoldsmiths([]);
    setCustomersSel([]);
    setOrderSel([]);
    setStages([]);
    setMaterials([]);
    setAgeBuckets([]);
    setJewelleryTypes([]);
    setReferenceSel([]);
    setRmStatus([]);
  };
  const removeFromArray = (setter, value) => {
    setter((prev) => prev.filter((v) => v !== value));
  };

  // Wave 4.x — Per-material style set for the redesigned Pending
  // Requirements grid cards. Reuses the module pastel tokens so each
  // material group reads as a tinted mini-table with a labelled header.
  const matStyle = (key) => ({
    gold: {
      chip: 'bg-[color:var(--gold-bg)] border-[color:var(--gold-border)] text-[color:var(--gold-icon)]',
      header: 'bg-[color:var(--gold-bg)]', dot: 'bg-amber-400',
    },
    diamond: {
      chip: 'bg-[color:var(--diamond-bg)] border-[color:var(--diamond-border)] text-[color:var(--diamond-icon)]',
      header: 'bg-[color:var(--diamond-bg)]', dot: 'bg-sky-400',
    },
    color_stone: {
      chip: 'bg-[color:var(--color-stones-bg)] border-[color:var(--color-stones-border)] text-[color:var(--color-stones-icon)]',
      header: 'bg-[color:var(--color-stones-bg)]', dot: 'bg-emerald-400',
    },
    pearl: {
      chip: 'bg-[color:var(--pearls-bg)] border-[color:var(--pearls-border)] text-[color:var(--pearls-icon)]',
      header: 'bg-[color:var(--pearls-bg)]', dot: 'bg-violet-400',
    },
  }[key] || {
    chip: 'bg-slate-50 border-slate-200 text-slate-600',
    header: 'bg-slate-50', dot: 'bg-slate-400',
  });

  const ageChipClass = (days) => {
    if (days >= 15) return 'bg-red-50 text-red-700 border border-red-100';
    if (days >= 8)  return 'bg-orange-50 text-orange-700 border border-orange-100';
    if (days >= 4)  return 'bg-amber-50 text-amber-700 border border-amber-100';
    return 'bg-slate-100 text-slate-600 border border-slate-200';
  };
  const ageLabel = (days) => {
    if (!days || days <= 0) return 'Today';
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  const downloadPdf = async () => {
    if (downloading) return;
    if (filteredPendingCount === 0) return;
    setDownloading(true);
    try {
      const params = { company_id: companyId };
      if (activeChipCount > 0) {
        params.order_ids = filteredOrders.map((o) => o.order_id);
        if (materials.length && showStageMaterialFilters) params.material_types = materials;
        if (jewelleryTypes.length) params.jewellery_types = jewelleryTypes;
        if (rmStatus.length) params.rm_status = rmStatus;
        if (ageBuckets.length) {
          const chosen = ageBuckets
            .map((bid) => _AGE_BUCKETS.find((b) => b.id === bid))
            .filter(Boolean);
          if (chosen.length) {
            const mins = chosen.map((b) => b.min);
            const maxs = chosen.map((b) => (b.max === null ? Number.MAX_SAFE_INTEGER : b.max));
            const lo = Math.min(...mins);
            const hi = Math.max(...maxs);
            params.age_min = lo;
            if (hi !== Number.MAX_SAFE_INTEGER) params.age_max = hi;
          }
        }
      }
      // Wave 4.04 — Honour the remembered "new page per Page No / Order No"
      // preference. Only send it when the toggle is actually shown (i.e. the
      // export spans more than one group), so a single-group sheet is
      // unaffected. Backend decides the concrete key from the company config.
      if (pageBreakPref && showPageBreakToggle) {
        params.page_break_by = refEnabled ? 'reference' : 'order';
      }
      // Requirement sort toggle — order the exported sheet by Order No
      // exactly like the on-screen list.
      params.sort = sortDir;
      const res = await api.get('/dashboard/pending-requirements/pdf', {
        params,
        paramsSerializer: { indexes: null },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const stem = activeChipCount > 0
        ? `pending-requirements-combined-${new Date().toISOString().slice(0, 10)}-filtered`
        : `pending-requirements-combined-${new Date().toISOString().slice(0, 10)}`;
      a.download = `${stem}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* noop */ }
    finally { setDownloading(false); }
  };

  const materialLabel = (key) => _MAT_LABELS[key] || key;
  const ageBucketLabel = (bid) => (_AGE_BUCKETS.find((x) => x.id === bid) || {}).label || bid;

  // Per-chip cascaded count. Same semantics as option counts — "how
  // many rows would remain if THIS were the only value selected in
  // its dimension".
  const chipCount = (skipDim, val) => {
    let c = 0;
    for (const o of (data.orders || [])) {
      const kept = _applyFilters(o, filterState, { skipDim, overrideValue: val });
      if (kept) c += kept.pending_count;
    }
    return c;
  };

  // Distinct running-numbers across the pending orders (for the header badge).
  const distinctReferenceCount = (() => {
    if (!refEnabled) return 0;
    const set = new Set();
    for (const o of (data.orders || [])) {
      if (o.reference_no != null) set.add(o.reference_no);
    }
    return set.size;
  })();

  // Wave 4.04 — Show the "new page per …" export toggle only when the
  // exported (filtered) set actually spans more than one group: more than
  // one Page No when reference numbers are enabled, otherwise more than one
  // Order No. When there's a single group the toggle is meaningless.
  const showPageBreakToggle = refEnabled
    ? filteredDistinctReferenceCount > 1
    : filteredOrders.length > 1;
  const pageBreakUnit = refEnabled ? refTerm : 'Order No.';

  return (
    <Card className="p-5" data-testid="dashboard-pending-requirements-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex-1 flex items-baseline justify-between gap-2 text-left min-w-0"
          data-testid="dashboard-pending-requirements-toggle"
        >
          <div className="min-w-0">
            <div className="text-base font-medium text-[var(--ink-navy)] flex items-center gap-2 flex-wrap">
              <ClipboardList size={14} /> Pending Requirements — {refEnabled ? `grouped by ${refTerm}, then Order` : 'grouped by Order'}
              {displayTotalPending > 0 && (activeChipCount > 0 ? (
                <>
                  <Badge className="bg-violet-50 text-violet-700 text-[10px]" data-testid="dashboard-pending-requirements-count">
                    {filteredPendingCount} of {displayTotalPending} pending
                  </Badge>
                  <Badge className="bg-white text-slate-600 border border-[var(--divider)] text-[10px]" data-testid="dashboard-pending-filtered-orders">
                    {filteredOrders.length} order{filteredOrders.length === 1 ? '' : 's'}
                  </Badge>
                  {refEnabled && (
                    <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px]" data-testid="dashboard-pending-filtered-refs">
                      {filteredDistinctReferenceCount} {refLabel.replace(/\.$/, '')}{filteredDistinctReferenceCount === 1 ? '' : 's'}
                    </Badge>
                  )}
                </>
              ) : (
                <Badge className="bg-violet-50 text-violet-700 text-[10px]" data-testid="dashboard-pending-requirements-count">
                  {`${displayTotalPending} pending in ${displayOrderCount} order${displayOrderCount === 1 ? '' : 's'}${refEnabled ? ` from ${distinctReferenceCount} ${refLabel.replace(/\.$/, '')}${distinctReferenceCount === 1 ? '' : 's'}` : ''}`}
                </Badge>
              ))}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Filtered to materials you can view · click any order to open its Requirements section
            </div>
          </div>
          <span className="text-slate-400 shrink-0 pt-1">{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
        </button>
        {open && displayTotalPending > 0 && (
          <div className="flex items-center gap-2 shrink-0">
            {/* Wave 3.99v — Grid ↔ Table toggle */}
            <div
              className="inline-flex rounded-md border border-[var(--divider)] bg-white overflow-hidden"
              data-testid="dashboard-pending-requirements-view-toggle"
            >
              <button
                type="button"
                onClick={() => setViewPersisted('grid')}
                className={`text-xs inline-flex items-center gap-1 px-2 py-1.5 ${view === 'grid' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid="dashboard-pending-view-grid"
                aria-pressed={view === 'grid'}
                title="Grid view"
              >
                <LayoutGrid size={12} /> Grid
              </button>
              <button
                type="button"
                onClick={() => setViewPersisted('table')}
                className={`text-xs inline-flex items-center gap-1 px-2 py-1.5 border-l border-[var(--divider)] ${view === 'table' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid="dashboard-pending-view-table"
                aria-pressed={view === 'table'}
                title="Table view"
              >
                <TableIcon size={12} /> Table
              </button>
            </div>
            {/* Sort toggle — order by Order No (Newest / Oldest). Matches
                the on-screen order and the combined PDF export. */}
            <div
              className="inline-flex rounded-md border border-[var(--divider)] bg-white overflow-hidden"
              data-testid="dashboard-pending-requirements-sort-toggle"
            >
              <button
                type="button"
                onClick={() => setSortPersisted('newest')}
                className={`text-xs inline-flex items-center gap-1 px-2 py-1.5 ${sortDir === 'newest' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid="dashboard-pending-sort-newest"
                aria-pressed={sortDir === 'newest'}
                title="Newest Order No first"
              >
                Newest
              </button>
              <button
                type="button"
                onClick={() => setSortPersisted('oldest')}
                className={`text-xs inline-flex items-center gap-1 px-2 py-1.5 border-l border-[var(--divider)] ${sortDir === 'oldest' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid="dashboard-pending-sort-oldest"
                aria-pressed={sortDir === 'oldest'}
                title="Oldest Order No first"
              >
                Oldest
              </button>
            </div>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={downloading || filteredPendingCount === 0}
              className={`text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-[var(--divider)] hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed ${hideExport ? 'hidden' : ''}`}
              data-testid="dashboard-pending-requirements-download-pdf"
              title={filteredPendingCount === 0 ? 'No rows to export' : 'Download combined PDF'}
            >
              <FileDown size={12} /> {downloading ? 'Preparing…' : 'Download combined PDF'}
            </button>
            {showPageBreakToggle && !hideExport && (
              <label
                className="text-xs inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-800 cursor-pointer select-none"
                data-testid="dashboard-pending-pagebreak-toggle"
                title={`Start each ${pageBreakUnit.replace(/\.$/, '')} on a new page in the combined PDF`}
              >
                <Checkbox
                  checked={pageBreakPref}
                  onCheckedChange={(v) => setPageBreakPersisted(!!v)}
                  data-testid="dashboard-pending-pagebreak-checkbox"
                />
                New page per {pageBreakUnit}
              </label>
            )}
          </div>
        )}
      </div>

      {open && (
        <div className="mt-4">
          {/* Filter chip bar */}
          {(data.orders.length > 0 || displayOrderCount > 0) && (
            <div
              className="mb-3 rounded-md border border-slate-100 bg-slate-50/60 px-2.5 py-2"
              data-testid="dashboard-pending-requirements-filter-bar"
            >
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 pr-1">
                  <Filter size={11} /> Filters
                </span>

                {search && (
                  <FilterChip
                    label={`Search: "${search}"`}
                    count={searchMatchCount}
                    onRemove={() => setSearch('')}
                    testId="filter-chip-search"
                  />
                )}
                {goldsmiths.map((g) => (
                  <FilterChip
                    key={`gs-${g}`}
                    label={`Goldsmith: ${g}`}
                    count={chipCount('goldsmith', g)}
                    onRemove={() => removeFromArray(setGoldsmiths, g)}
                    testId={`filter-chip-goldsmith-${g}`}
                  />
                ))}
                {customersSel.map((c) => (
                  <FilterChip
                    key={`cs-${c}`}
                    label={`Customer: ${c}`}
                    count={chipCount('customer', c)}
                    onRemove={() => removeFromArray(setCustomersSel, c)}
                    testId={`filter-chip-customer-${c}`}
                  />
                ))}
                {orderSel.map((oid) => (
                  <FilterChip
                    key={`ord-${oid}`}
                    label={`Order: ${orderLabelById[oid] || oid}`}
                    count={chipCount('order', oid)}
                    onRemove={() => removeFromArray(setOrderSel, oid)}
                    testId={`filter-chip-order-${oid}`}
                  />
                ))}
                {refEnabled && referenceSel.map((ref) => (
                  <FilterChip
                    key={`ref-${ref}`}
                    label={`${refLabelShort}: ${ref}`}
                    count={chipCount('reference', ref)}
                    onRemove={() => removeFromArray(setReferenceSel, ref)}
                    testId={`filter-chip-reference-${ref}`}
                  />
                ))}
                {jewelleryTypes.map((j) => (
                  <FilterChip
                    key={`jt-${j}`}
                    label={`Jewellery: ${j}`}
                    count={chipCount('jewellery', j)}
                    onRemove={() => removeFromArray(setJewelleryTypes, j)}
                    testId={`filter-chip-jewellery-${j}`}
                  />
                ))}
                {showStageMaterialFilters && stages.map((s) => {
                  // Wave 3.99w — Use the canonical human label from
                  // the grouped taxonomy so the chip reads "Stage:
                  // CAD Active" instead of the raw step key "cad".
                  const canon = [..._STAGE_LEGACY, ..._STAGE_STEPS].find((x) => x.id === s);
                  return (
                    <FilterChip
                      key={`st-${s}`}
                      label={`Stage: ${canon ? canon.label : s}`}
                      count={chipCount('stage', s)}
                      onRemove={() => removeFromArray(setStages, s)}
                      testId={`filter-chip-stage-${s}`}
                    />
                  );
                })}
                {showStageMaterialFilters && materials.map((m) => (
                  <FilterChip
                    key={`mt-${m}`}
                    label={`Material: ${materialLabel(m)}`}
                    count={chipCount('material', m)}
                    onRemove={() => removeFromArray(setMaterials, m)}
                    testId={`filter-chip-material-${m}`}
                  />
                ))}
                {rmStatus.map((s) => (
                  <FilterChip
                    key={`rm-${s}`}
                    label={`RM: ${_RM_STATUS_LABELS[s] || s}`}
                    count={chipCount('rmStatus', s)}
                    onRemove={() => removeFromArray(setRmStatus, s)}
                    testId={`filter-chip-rm-status-${s}`}
                  />
                ))}
                {ageBuckets.map((b) => (
                  <FilterChip
                    key={`ab-${b}`}
                    label={`Age: ${ageBucketLabel(b)}`}
                    count={chipCount('age', b)}
                    onRemove={() => removeFromArray(setAgeBuckets, b)}
                    testId={`filter-chip-age-${b}`}
                  />
                ))}

                {/* Add-filter menu — one popover per dimension.  */}
                <MultiSelectPopover
                  triggerLabel="Goldsmith"
                  options={goldsmithOptions}
                  selected={goldsmiths}
                  onChange={setGoldsmiths}
                  testId="filter-add-goldsmith"
                  placeholder="Search goldsmiths…"
                />
                <MultiSelectPopover
                  triggerLabel="Customer"
                  options={customerOptions}
                  selected={customersSel}
                  onChange={setCustomersSel}
                  testId="filter-add-customer"
                  placeholder="Search customers…"
                />
                <MultiSelectPopover
                  triggerLabel="Order"
                  options={orderOptions}
                  selected={orderSel}
                  onChange={setOrderSel}
                  testId="filter-add-order"
                  placeholder="Search order no or title…"
                />
                {refEnabled && (
                  <MultiSelectPopover
                    triggerLabel={refLabelShort}
                    options={referenceOptions}
                    selected={referenceSel}
                    onChange={setReferenceSel}
                    testId="filter-add-reference"
                    placeholder={`Search ${refLabelShort.toLowerCase()}…`}
                  />
                )}
                <MultiSelectPopover
                  triggerLabel="Jewellery"
                  options={jewelleryOptions}
                  selected={jewelleryTypes}
                  onChange={setJewelleryTypes}
                  testId="filter-add-jewellery"
                  placeholder="Search jewellery types…"
                />
                {showStageMaterialFilters && (
                  <MultiSelectPopover
                    triggerLabel="Stage"
                    options={stageOptions}
                    selected={stages}
                    onChange={setStages}
                    testId="filter-add-stage"
                    placeholder="Search stages…"
                    groups={stageGroups}
                  />
                )}
                {showStageMaterialFilters && (
                  <MultiSelectPopover
                    triggerLabel="Material"
                    options={materialOptions}
                    selected={materials}
                    onChange={setMaterials}
                    testId="filter-add-material"
                    placeholder="Filter materials…"
                  />
                )}
                {rmStatusOptions.length > 0 && (
                  <MultiSelectPopover
                    triggerLabel="RM Status"
                    options={rmStatusOptions}
                    selected={rmStatus}
                    onChange={setRmStatus}
                    testId="filter-add-rm-status"
                    placeholder="Filter raw material status…"
                  />
                )}
                <MultiSelectPopover
                  triggerLabel="Age"
                  options={ageBucketOptions}
                  selected={ageBuckets}
                  onChange={setAgeBuckets}
                  testId="filter-add-age"
                  placeholder="Filter age…"
                />

                {activeChipCount >= 2 && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-[11px] text-slate-500 hover:text-slate-800 underline ml-auto"
                    data-testid="dashboard-pending-requirements-clear-all"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Inline free-text search — kept below chips so buyers can
                  type-to-narrow without opening a popover. */}
              <div className="mt-2">
                <div className="relative">
                  <Search
                    size={11}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by order no or title…"
                    data-testid="dashboard-pending-requirements-search"
                    className="h-8 pl-7 text-xs"
                  />
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-6 text-center text-sm text-slate-500">Loading…</div>
          ) : data.orders.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500" data-testid="dashboard-pending-requirements-empty">
              No pending requirements in your material scope.
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500" data-testid="dashboard-pending-requirements-search-empty">
              No orders match the current filters. Try removing a chip or clearing filters.
            </div>
          ) : (
            <>
              {pendingTotalPages > 1 && (
                <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="dashboard-pending-range-tabs">
                  {Array.from({ length: pendingTotalPages }, (_, i) => {
                    const from = i * PENDING_PAGE_SIZE + 1;
                    const to = Math.min((i + 1) * PENDING_PAGE_SIZE, pendingTotalOrders);
                    const active = i === pendingCurrent;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPendingPage(i)}
                        aria-pressed={active}
                        data-testid={`dashboard-pending-range-${from}-${to}`}
                        className={`rounded-md border px-2.5 py-1 text-[11px] font-mono tabular-nums transition-colors ${active ? 'border-violet-500 bg-violet-500 text-white' : 'border-[var(--divider)] bg-white text-slate-600 hover:border-violet-400'}`}
                      >
                        {from}-{to}
                      </button>
                    );
                  })}
                  {!fullyLoaded && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 ml-1" data-testid="dashboard-pending-loading-hint">
                      <Loader2 size={11} className="animate-spin" /> loading all…
                    </span>
                  )}
                </div>
              )}
              {view === 'table' ? (
            /* ------------------------------------------------------
               Wave 3.99v — Compact table view. One row per order with
               a summary of pending materials. Buyers who prefer the
               density of a spreadsheet get this by default when they
               toggle above.
               ------------------------------------------------------ */
            <div className="overflow-x-auto border border-slate-100 rounded-md" data-testid="dashboard-pending-requirements-table">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr className="text-left">
                    <th className="px-2 py-2 font-medium">Order</th>
                    <th className="px-2 py-2 font-medium">Customer</th>
                    <th className="px-2 py-2 font-medium">Goldsmith</th>
                    <th className="px-2 py-2 font-medium">Stage</th>
                    <th className="px-2 py-2 font-medium">Jewellery</th>
                    <th className="px-2 py-2 font-medium">Delivery</th>
                    <th className="px-2 py-2 font-medium text-right">Pending</th>
                    <th className="px-2 py-2 font-medium">Materials</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {referenceGroups.map((g) => (
                    <React.Fragment key={g.ref ?? '__none__'}>
                      {g.label && (
                        <tr className="bg-violet-50/50" data-testid={`dashboard-pending-ref-group-${g.ref ?? 'none'}`}>
                          <td colSpan={8} className="px-2 py-1.5">
                            <div className="flex items-center gap-2 text-[11px] font-semibold text-[var(--ink-navy)]">
                              <span className="inline-flex items-center font-mono text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">{g.label}</span>
                              <span className="text-slate-400 font-normal">{g.orders.length} order{g.orders.length === 1 ? '' : 's'}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {g.orders.map((o) => {
                    const totalRows = _pendingCount(o);
                    return (
                      <tr
                        key={o.order_id}
                        className="hover:bg-slate-50/60"
                        data-testid={`dashboard-pending-requirements-row-${o.order_no}`}
                      >
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <OrderThumb
                              url={o.thumbnail_url}
                              size={28}
                              testId={`dashboard-pending-thumb-${o.order_no}`}
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Link
                                  to={`/orders/${o.order_id}#order-requirements`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[12.5px] font-medium text-[var(--ink-navy)] hover:underline"
                                  data-testid={`dashboard-pending-requirements-order-link-${o.order_no}`}
                                >
                                  {o.order_no}
                                </Link>
                                {refEnabled && o.reference_no != null && (
                                  <InlineReference
                                    referenceNo={o.reference_no}
                                    labelShort={refLabelShort}
                                    testId={`dashboard-pending-page-no-${o.order_no}`}
                                  />
                                )}
                              </div>
                              {o.title && (
                                <div className="text-[11px] text-slate-500 truncate max-w-[220px]" title={o.title}>{o.title}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-slate-700">{o.customer_name || '—'}</td>
                        <td className="px-2 py-2 text-slate-700">{o.goldsmith_name || '—'}</td>
                        <td className="px-2 py-2 text-slate-700">{o.stage || '—'}</td>
                        <td className="px-2 py-2 text-slate-700">{o.jewellery_type || '—'}</td>
                        <td className="px-2 py-2 text-slate-700">{o.delivery_date || '—'}</td>
                        <td className="px-2 py-2 text-right">
                          <span className="inline-flex items-center rounded bg-slate-100 text-slate-700 px-1.5 py-0.5 text-[10.5px] font-mono tabular-nums">
                            {totalRows}
                            {typeof o.total_requirements === 'number' ? ` / ${o.total_requirements}` : ''}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(o.groups || {}).map(([matKey, rows]) => (
                              <span
                                key={matKey}
                                className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-1.5 py-0.5 text-[10.5px] text-slate-700"
                                data-testid={`dashboard-pending-mat-pill-${o.order_no}-${matKey}`}
                              >
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${{
                                  gold: 'bg-amber-400',
                                  diamond: 'bg-sky-400',
                                  color_stone: 'bg-emerald-400',
                                  pearl: 'bg-violet-400',
                                }[matKey] || 'bg-slate-400'}`} />
                                {materialLabel(matKey)}
                                <span className="text-slate-400 font-mono tabular-nums">{rows.length}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* ------------------------------------------------------
               Grid view — one card per order with material groups
               nested inside. This is the historical default.
               ------------------------------------------------------ */
            <div className="space-y-4">
              {referenceGroups.map((g) => (
                <div key={g.ref ?? '__none__'} className="space-y-2" data-testid={`dashboard-pending-ref-group-grid-${g.ref ?? 'none'}`}>
                  {g.label && (
                    <div className="flex items-center gap-2 border-l-4 border-l-indigo-400 pl-2.5 py-1 bg-indigo-50/50 rounded-r-md">
                      <span className="inline-flex items-center font-mono text-[11px] text-indigo-700 bg-white border border-indigo-200 px-2 py-0.5 rounded">{g.label}</span>
                      <span className="text-[11px] text-slate-500">{g.orders.length} order{g.orders.length === 1 ? '' : 's'}</span>
                    </div>
                  )}
                  {g.orders.map((o) => {
                const totalRows = _pendingCount(o);
                return (
                  <Card
                    key={o.order_id}
                    className="p-3 border-l-4 border-l-violet-300"
                    data-testid={`dashboard-pending-requirements-order-${o.order_no}`}
                  >
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex items-start gap-2">
                        <OrderThumb
                          url={o.thumbnail_url}
                          size={40}
                          testId={`dashboard-pending-thumb-${o.order_no}`}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Link
                              to={`/orders/${o.order_id}#order-requirements`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-[var(--ink-navy)] hover:underline"
                              data-testid={`dashboard-pending-requirements-order-link-${o.order_no}`}
                            >
                              {o.order_no}{o.title ? ` — ${o.title}` : ''}
                            </Link>
                            {refEnabled && o.reference_no != null && (
                              <InlineReference
                                referenceNo={o.reference_no}
                                labelShort={refLabelShort}
                                testId={`dashboard-pending-page-no-${o.order_no}`}
                              />
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                            {o.customer_name && <span>{o.customer_name}</span>}
                            {o.customer_name && o.goldsmith_name && <span>·</span>}
                            {o.goldsmith_name && (
                              <span>
                                <span className="text-slate-400">Goldsmith:</span> {o.goldsmith_name}
                              </span>
                            )}
                            {o.jewellery_type && <span>·</span>}
                            {o.jewellery_type && <span>{o.jewellery_type}</span>}
                            {o.stage && <span>·</span>}
                            {o.stage && <span>{o.stage}</span>}
                            {o.delivery_date && <><span>·</span><span>Delivery {o.delivery_date}</span></>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge className="bg-slate-100 text-slate-700 text-[10px]">
                          {totalRows} pending
                          {typeof o.total_requirements === 'number'
                            ? ` of ${o.total_requirements}`
                            : ''}
                        </Badge>
                      </div>
                    </div>

                    <div className="mt-3 space-y-2.5" data-testid={`dashboard-pending-materials-${o.order_no}`}>
                      {Object.entries(o.groups || {}).map(([matKey, rows]) => {
                        const isGold = matKey === 'gold';
                        const unit = isGold ? 'g' : 'ct · pc';
                        const ms = matStyle(matKey);
                        return (
                          <div
                            key={matKey}
                            className="rounded-lg border border-[color:var(--divider)] bg-white overflow-hidden"
                            data-testid={`dashboard-pending-material-group-${o.order_no}-${matKey}`}
                          >
                            {/* Material header + aligned column headings */}
                            <div className={`flex items-center justify-between gap-2 px-2.5 py-1.5 border-b border-[color:var(--divider)] ${ms.header}`}>
                              <span
                                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] ${ms.chip}`}
                                data-testid={`dashboard-pending-material-chip-${o.order_no}-${matKey}`}
                              >
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${ms.dot}`} />
                                {materialLabel(matKey)}
                              </span>
                              {/* Desktop-only headings aligned to the row's numeric columns */}
                              <div
                                className="hidden sm:grid grid-cols-[64px_132px_132px] items-end gap-2 text-[9.5px] uppercase tracking-[0.08em] font-semibold text-slate-500"
                                data-testid={`dashboard-pending-headings-${o.order_no}-${matKey}`}
                              >
                                <span className="text-right">Age</span>
                                <span className="text-right">Balance <span className="text-slate-400 normal-case tracking-normal font-normal">({unit})</span></span>
                                <span className="text-right">Required <span className="text-slate-400 normal-case tracking-normal font-normal">({unit})</span></span>
                              </div>
                            </div>
                            {/* Requirement lines — aligned mini-grid (no dead-gap) */}
                            <div className="divide-y divide-[color:var(--divider)]">
                              {rows.map((r) => {
                                const req = isGold
                                  ? `${(r.required_grams || 0).toFixed(3)} g`
                                  : `${(r.required_carats || 0).toFixed(3)} ct · ${r.required_pieces || 0} pc`;
                                const bal = isGold
                                  ? `${(r.remaining_grams || 0).toFixed(3)} g`
                                  : `${(r.remaining_carats || 0).toFixed(3)} ct · ${r.remaining_pieces || 0} pc`;
                                const label = r.classification_label || r.description || 'Requirement';
                                return (
                                  <div
                                    key={r.id}
                                    className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_64px_132px_132px] items-center gap-x-2 gap-y-1 px-2.5 py-1.5 hover:bg-[color:var(--paper-warm)] transition-colors"
                                    data-testid={`dashboard-pending-line-${r.id}`}
                                  >
                                    {/* Item label */}
                                    <div
                                      className="min-w-0 truncate text-[12.5px] font-medium text-slate-800"
                                      title={label}
                                      data-testid={`dashboard-pending-line-label-${r.id}`}
                                    >
                                      {label}
                                    </div>
                                    {/* Age chip */}
                                    <div className="justify-self-end">
                                      <span
                                        className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-[1px] text-[9.5px] font-medium leading-none whitespace-nowrap ${ageChipClass(r.age_days)}`}
                                        data-testid={`dashboard-req-age-${r.id}`}
                                        title="Age of this requirement"
                                      >
                                        <Timer size={9} /> {ageLabel(r.age_days)}
                                      </span>
                                    </div>
                                    {/* Desktop numeric columns */}
                                    <div
                                      className="hidden sm:block justify-self-end text-right font-mono tabular-nums text-[12px] whitespace-nowrap req-balance"
                                      data-testid={`dashboard-pending-line-balance-${r.id}`}
                                    >
                                      {bal}
                                    </div>
                                    <div
                                      className="hidden sm:block justify-self-end text-right font-mono tabular-nums text-[12px] whitespace-nowrap text-slate-600"
                                      data-testid={`dashboard-pending-line-required-${r.id}`}
                                    >
                                      {req}
                                    </div>
                                    {/* Mobile numeric block (labelled) */}
                                    <div className="sm:hidden col-span-2 grid grid-cols-2 gap-2">
                                      <div className="rounded-md bg-[color:var(--paper-warm)] px-2 py-1">
                                        <div className="text-[9px] uppercase tracking-[0.08em] font-semibold text-slate-500">Balance <span className="text-slate-400 normal-case tracking-normal font-normal">({unit})</span></div>
                                        <div className="font-mono tabular-nums text-[11.5px] req-balance">{bal}</div>
                                      </div>
                                      <div className="rounded-md bg-[color:var(--paper-warm)] px-2 py-1">
                                        <div className="text-[9px] uppercase tracking-[0.08em] font-semibold text-slate-500">Required <span className="text-slate-400 normal-case tracking-normal font-normal">({unit})</span></div>
                                        <div className="font-mono tabular-nums text-[11.5px] text-slate-600">{req}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
                  })}
                </div>
              ))}
            </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}
